// ============================================================================
// WORK ITEMS VIEW WRAPPER
// Wrapper component that manages data for both List and Tree views
// Date: October 25, 2025
// Updated: Now using universal work item components with WorkItemDetailModal
// Updated: December 2025 - Added comprehensive search/filter/sort via useWorkItemsFilter
// ============================================================================

'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-operations/workflows/service-operations/views.mdx
 * (published at user-docs.totalaccess.co.za/total-operations/workflows/service-operations/views)
 */

import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { List, Workflow, LayoutGrid, GanttChart as GanttIcon, Calendar as CalendarIcon, Plus, UserCheck, Bell, Loader2, Target, Zap, Bot } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { useResponsive } from '@/hooks/useResponsive';
import { useAuth } from '@/hooks/useAuth';
import { useOptionalWorkItemOpener } from '@/hooks/useWorkItemOpener';
import ModulePageShell from '@/components/modules/ModulePageShell';
import type { ModuleHeaderProps } from '@/components/modules/ModuleHeader';
import type { StrictModuleHeaderProps, StrictStatCard, StrictFilterPill } from '@/components/modules/StrictModuleHeader';
import WorkItemTree, { type TreeVisibleColumnKey, type TreeWorkItem } from '@/components/WorkItemTree';
import WorkItemsKanbanBoard, { type BoardToolbarModel } from '@/components/WorkItemsKanbanBoard';
import WorkItemsTimelineView from '@/components/WorkItemsTimelineView';
import WorkItemsCalendarView from '@/components/WorkItemsCalendarView';
import WorkItemsSLA from '@/components/WorkItemsSLA';
import WorkItemsDataGrid, {
  getWorkItemsDataGridColumns,
  getWorkItemsDataGridToggleableColumnKeys,
  sanitizeWorkItemsDataGridVisibleColumns,
  NON_TOGGLEABLE_WORK_ITEMS_GRID_COLUMN_KEYS,
  type WorkItem as DataGridWorkItem,
  type WorkItemsDataGridColumnDef,
  type WorkItemsDataGridStyleVariant,
  type ItemTypeOption as DataGridItemTypeOption,
} from '@/components/WorkItemsDataGrid';
import WorkItemDetailModal, { type WorkItemTabId } from '@/components/WorkItemDetailModal';
import WorkItemCreateDropdown from '@/components/WorkItemCreateDropdown';
import LocationPickerModal from '@/components/LocationPickerModal';
import { SectionLoader } from '@/components/shared/BrandedLoadingSpinner';
import UserDocsLink from '@/components/shared/UserDocsLink';
// NEW: Bulk Actions Bar (February 2026)
import BulkActionsBar from '@/components/shared/BulkActionsBar';
// NEW: Open team member profile from avatar click (February 2026)
import { useOpenTeamMember } from '@/hooks/useOpenTeamMember';
// Advanced view modes (configurable for any template)
import ProductBacklogView from '@/app/client-zone/modules/total-operations/components/ProductBacklogView';
import SprintView from '@/app/client-zone/modules/total-operations/components/SprintView';
import RoadmapView from '@/app/client-zone/modules/total-operations/components/RoadmapView';
import AnalyticsView from '@/app/client-zone/modules/total-operations/components/AnalyticsView';
import { appendWorkItemsDateRangeParams } from '@/hooks/useWorkItemsData';
import { useWorkItemsWithResolvedStatus } from '@/hooks/useWorkItemsWithResolvedStatus';
import type { WorkItem as DataWorkItem } from '@/hooks/useWorkItemsData';
import useAppointmentLiveTimers from '@/hooks/useAppointmentLiveTimers';
import { useWorkItemHierarchy, type WorkItem as HierarchyWorkItem } from '@/hooks/useWorkItemHierarchy';
import type { WorkItem, WorkItemAssignee } from '@/components/UniversalWorkItems';
import { authService } from '@/lib/authService';
import { on } from '@/lib/typedEventBus';
import { notify, confirm } from '@/lib/notifications';
import { invoiceMatchesListStatusTab } from '@/lib/finance/invoiceListUi';
import {
  downloadRecordsAsCsv,
  downloadRecordsAsJson,
  downloadRecordsAsXlsx,
  type ExportColumnDef,
  type ExportFormat,
} from '@/lib/export/clientExport';
import {
  prepareAdminAppointmentOverride,
  resolveAppointmentAdminOverrideAudience,
  type PreparedAdminAppointmentOverrideRequest,
} from '@/lib/work-items/adminAppointmentOverrideClient';
import { jobStatusBlocksAppointmentCreation, normalizeWorkflowStatusKey } from '@/lib/work-items/operationalStatusConfig';
import { isWorkflowActionPermitted, useWorkflowActionPermissions } from '@/lib/operations/workflowActionPermissions';
// NEW: Comprehensive filter/sort system (December 2025)
import { applyWorkItemFilterRules, useWorkItemsFilter } from '@/hooks/useWorkItemsFilter';
import type { FilterConfig, WorkItemStatFilter } from '@/hooks/useWorkItemsFilter';
import WorkItemsFilterBar from '@/components/WorkItemsFilterBar';
import type { ContactOption } from '@/components/filters/ContactSearchFilter';
// NEW: Dynamic type filter configuration (January 2026)
import { useTypeFilterConfig } from '@/hooks/useTypeFilterConfig';
// NEW: Dynamic workflow statuses from config (January 2026)
import { useWorkflowStatuses } from '@/hooks/useWorkflowStatuses';
// NEW: Client's kanban style preference (January 2026)
import { useKanbanStylePreference } from '@/hooks/useKanbanStylePreference';
import type { AssignedUser } from '@/components/kanban/kanban-shared';
import { getWorkItemTypeIdentity, primeTypeIdentityCache } from '@/lib/work-items/typeIdentity';
import { useWorkItemTypes } from '@/hooks/useWorkItemTypes';
import { networkState } from '@/lib/networkState';
import { wsClient, type ConnectionStatus } from '@/lib/websocket/TotalAccessWebSocket';
import {
  buildHiddenColumnPreference,
  resolveVisibleColumnsFromSerializedPreferenceString,
  resolveVisibleColumnsFromStoredPreference,
} from '@/lib/work-items/columnVisibilityPreferences';
import { buildWorkItemsViewPreferenceScopeKey } from '@/lib/work-items/viewPreferenceScopes';
import { buildWorkItemsKanbanForwardedProps } from '@/lib/work-items/viewWrapperBoardProps';
import { enrichJobExportRecordsWithChildAppointments } from '@/lib/work-items/workItemExportClipboard';
import { logUiAudit } from '@/lib/audit/uiAudit';

/** Coalesce burst filter/tab changes before hitting the counts_only API. */
const SERVER_STATUS_COUNTS_DEBOUNCE_MS = 350;

// ============================================================================
// TYPES
// ============================================================================

type ViewMode = 'list' | 'tree' | 'gantt' | 'board' | 'calendar' | 'sla' | 'backlog' | 'sprint' | 'roadmap' | 'analytics';

export interface WorkItemAssigneeOption {
  id: number;
  name: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  role?: string;
  isInactive?: boolean;
  membershipStatus?: string;
  invitationStatus?: string;
}

type WorkItemLike = WorkItem | DataWorkItem | TreeWorkItem;

interface TeamMemberUserRecord {
  id?: number | string | null;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  primary_email?: string | null;
  personal_email?: string | null;
}

interface TeamMemberRecord extends TeamMemberUserRecord {
  status?: string | null;
  invitation_status?: string | null;
  user?: TeamMemberUserRecord | null;
  user_id?: number | string | null;
  role?: string | null;
}

interface DirectoryAssigneeRecord {
  id?: number | string | null;
  is_active?: boolean | null;
  employment_status?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  primary_email?: string | null;
  business_email?: string | null;
  personal_email?: string | null;
  name?: string | null;
  role?: string | null;
  module_role?: string | null;
}

interface ExtendedWorkItem extends WorkItem {
  assigned_to_name?: string | null;
  contact_type?: string | null;
  current_workflow_stage?: string | null;
  custom_fields?: Record<string, unknown>;
}

interface WorkItemsByIdResponse {
  data?: ExtendedWorkItem[];
}

interface PaymentReminderCandidate extends WorkItem {
  doc_number?: string | null;
}

function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

function getAssignedToName(item: WorkItemLike): string | null {
  const extendedItem: ExtendedWorkItem = item;
  const assignedToName = extendedItem.assigned_to_name;

  return typeof assignedToName === 'string' && assignedToName.trim().length > 0
    ? assignedToName.trim()
    : null;
}

function getContactType(item: WorkItemLike): string {
  const extendedItem: ExtendedWorkItem = item;
  return typeof extendedItem.contact_type === 'string' && extendedItem.contact_type.trim().length > 0
    ? extendedItem.contact_type
    : 'customer';
}

function getWorkItemCustomFields(item: WorkItemLike): Record<string, unknown> {
  const extendedItem: ExtendedWorkItem = item;
  return extendedItem.custom_fields ?? {};
}

function normalizeAssigneeId(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const normalized = Number(value.trim());
    return Number.isFinite(normalized) ? normalized : null;
  }

  return null;
}

function normalizeQueryFilterValues(value: string | string[] | null | undefined): string[] {
  if (!value) {
    return [];
  }

  const values = Array.isArray(value) ? value : String(value).split(',');
  return values
    .map((entry) => String(entry || '').trim())
    .filter(Boolean);
}

function mergeScopedFilterValues(scopeValues: string[], selectedValues: string[]): { values: string[]; hasEmptyIntersection: boolean } {
  if (selectedValues.length === 0) {
    return { values: scopeValues, hasEmptyIntersection: false };
  }

  if (scopeValues.length === 0) {
    return { values: selectedValues, hasEmptyIntersection: false };
  }

  const scopeSet = new Set(scopeValues);
  const intersection = selectedValues.filter((value) => scopeSet.has(value));
  return {
    values: intersection,
    hasEmptyIntersection: intersection.length === 0,
  };
}

function mapTeamMemberAssignee(
  member: TeamMemberRecord,
  options: { includeInactive?: boolean } = {}
): WorkItemAssigneeOption | null {
  const memberStatus = typeof member.status === 'string' ? member.status : 'active';
  const invitationStatus = typeof member.invitation_status === 'string' ? member.invitation_status : 'accepted';
  const user = member.user && typeof member.user === 'object' ? member.user : member;
  const id = normalizeAssigneeId(user.id ?? member.user_id ?? member.id);

  if (!id) {
    return null;
  }

  if (invitationStatus === 'pending') {
    return null;
  }

  if (memberStatus !== 'active' && !options.includeInactive) {
    return null;
  }

  const firstName = typeof user.first_name === 'string' ? user.first_name.trim() : '';
  const lastName = typeof user.last_name === 'string' ? user.last_name.trim() : '';
  const email = [user.email, user.primary_email, user.personal_email]
    .find((value): value is string => typeof value === 'string' && value.trim().length > 0)
    ?.trim();
  const name = [firstName, lastName].filter(Boolean).join(' ') || email || `User ${id}`;

  return {
    id,
    name,
    email,
    first_name: firstName || undefined,
    last_name: lastName || undefined,
    role: typeof member.role === 'string' ? member.role : undefined,
    isInactive: memberStatus !== 'active',
    membershipStatus: memberStatus,
    invitationStatus,
  };
}

function mapAssigneeOption(person: DirectoryAssigneeRecord): WorkItemAssigneeOption | null {
  const id = typeof person.id === 'number' ? person.id : Number(person.id);
  if (!Number.isFinite(id)) {
    return null;
  }

  if (person.is_active === false || person.employment_status === 'terminated') {
    return null;
  }

  const firstName = typeof person.first_name === 'string' ? person.first_name.trim() : '';
  const lastName = typeof person.last_name === 'string' ? person.last_name.trim() : '';
  const email = [person.email, person.primary_email, person.business_email, person.personal_email]
    .find((value): value is string => typeof value === 'string' && value.trim().length > 0)
    ?.trim();
  const name = typeof person.name === 'string' && person.name.trim().length > 0
    ? person.name.trim()
    : [firstName, lastName].filter(Boolean).join(' ') || email || `User ${id}`;

  return {
    id,
    name,
    email,
    first_name: firstName || undefined,
    last_name: lastName || undefined,
    role: typeof person.role === 'string'
      ? person.role
      : (typeof person.module_role === 'string' ? person.module_role : undefined),
  };
}

function toAssignedUser(assignee: WorkItemAssigneeOption): AssignedUser {
  const normalizedName = assignee.name?.trim() || '';
  const [derivedFirstName, ...derivedLastName] = normalizedName.split(/\s+/).filter(Boolean);

  return {
    id: assignee.id,
    email: assignee.email || '',
    first_name: assignee.first_name || derivedFirstName || assignee.email || 'User',
    last_name: assignee.last_name || (derivedLastName.length > 0 ? derivedLastName.join(' ') : undefined),
    role: assignee.role,
    isInactive: assignee.isInactive,
  };
}

interface WorkItemsViewWrapperProps {
  activeTab: string;
  workItemType?: string; // Optional - can filter by module instead
  workItemSubtype?: string; // Optional - filter by item_subtype (e.g., 'delivery', 'pickup')
  templateKey?: string;
  initialItemId?: string;
  moduleContext?: string; // Filter by module_context (e.g., 'software-development')
  teamId?: string; // Active team scope for authoritative team-member assignee lists
  solutionKey?: string; // Active solution scope for team-filtered assignee lists in Total Operations
  workspaceId?: string; // Filter by workspace_id (Jira-like workspaces)
  workflowStates?: Array<{
    key: string;
    label: string;
    color: string;
    description?: string;
    isSystem?: boolean;
  }>;
  hideViewToggle?: boolean; // Hide the view mode toggle (when parent controls it)
  /** When true and `strictHeaderProps` is set, the view mode toggle (List /
   *  Board / Calendar / Gantt / …) renders as Row D filter pills in the
   *  StrictModuleHeader instead of the inline toggle bar. */
  viewToggleInHeader?: boolean;
  defaultViewMode?: ViewMode; // Default view mode when component mounts (default: 'list')
  /** When provided, only show toggle buttons for these view modes (others hidden). */
  enabledViewModes?: ViewMode[]; // Restrict available view modes (e.g., from workspace config)
  /** When true, items cannot be mutated from this surface — board/calendar
   *  drags, inline date edits and bulk-bar mutations are all disabled. For
   *  lifecycle-managed item types (e.g. voting) whose status/dates are owned
   *  by bespoke APIs rather than the generic work-items PATCH. */
  readOnly?: boolean;
  enableSlaView?: boolean; // Show the reusable SLA tracking view when relevant
  onConfigureSla?: () => void; // Optional handler to open SLA configuration from the SLA view
  hideFilters?: boolean; // Hide the entire filter/create section (when parent provides it)
  filterByAssignedUser?: boolean; // Filter to show only items assigned to current user
  assignedOnly?: boolean; // When true with filterByAssignedUser, excludes items only matching created_by
  personalMode?: boolean; // NEW: Personal mode - only show items assigned to current user with client-side safety filter (February 2026)
  assignedUserScope?: number[]; // Enforced assignee scope applied before filter-bar assignee narrowing
  additionalFilters?: Record<string, string | number>; // NEW: Additional API query params to pass through (e.g., { source_mailbox_id: 5 })
  /** When true (finance invoices), list also includes platform-issued subscription invoices (client 1, metadata.client_id = tenant). */
  includePlatformSubscriptionInvoices?: boolean;
  permissions?: {
    can_view: boolean;
    can_create: boolean;
    can_edit: boolean;
    can_delete: boolean;
    can_admin: boolean;
  };
  enableItemTypeFilter?: boolean; // Show item type filter pills (for software dev, etc.)
  showAllFilter?: boolean; // Show "All" button in item type filter (default: true)
  autoLoadTypeFilters?: boolean; // NEW: Auto-load type filters from work_item_number_sequences config (January 2026)
  itemTypeOptions?: Array<{ // Configurable item types for filter
    id: string;
    label: string;
    icon: React.ElementType;
    color: string;
    hoverColor: string;
    description?: string;
  }>;
  customFilterButtons?: Array<{ // Custom action buttons to show alongside item type filters
    id: string;
    label: string;
    icon: React.ElementType;
    color: string;
    hoverColor?: string;
    description?: string;
    onClick: () => void;
  }>;
  showCreateDropdown?: boolean; // Show universal create dropdown instead of basic "New Item" button
  onItemTypeSelect?: (itemType: string) => void; // Callback when user selects item type from create dropdown
  workflowField?: string; // Field to use for workflow state (default: 'status', supports dotted paths like 'custom_fields.collection_workflow_stage')
  boardKey?: string; // Optional persisted board key override for Kanban layouts
  statusFilter?: string[]; // Only show items with these statuses (e.g., ['sent', 'outstanding', 'overdue'])
  excludeStatuses?: string[]; // Hide items with these statuses (e.g., ['pending_approval', 'pending_operations_approval'])
  // NEW: Filter bar options (December 2025)
  showFilterBar?: boolean; // Show the comprehensive filter bar
  filterBarCompact?: boolean; // Use compact filter bar layout
  filterBarSingleRow?: boolean; // Keep search + filters in a single horizontal row on desktop
  showFilterBarStats?: boolean; // Show filtering stats
  showFilterBarQuickFilters?: boolean; // Show quick filter buttons
  showFilterBarPresets?: boolean; // Show saved view presets
  showFilterBarStatusFilter?: boolean; // Show status dropdown inside filter bar
  showFilterBarPriorityFilter?: boolean; // Show priority dropdown inside filter bar
  showFilterBarAssigneeFilter?: boolean; // Show assignee dropdown inside filter bar
  showFilterBarTypeFilter?: boolean; // Show item type/subtype dropdown inside filter bar
  showFilterBarDateFilter?: boolean; // Show date range picker inside filter bar
  showFilterBarSortControls?: boolean; // Show sort dropdown inside filter bar
  availableAssignees?: WorkItemAssigneeOption[]; // Team members for assignee filter
  hideAssigneeSelectAll?: boolean; // Hide "Select all" option in assignee filter (for personal mode)
  attentionCount?: number; // Optional attention count to display in filter bar (e.g., for ManagedTasksModal)
  onAttentionCountClick?: () => void; // Optional callback when attention count pill is clicked
  mobileTabToggle?: {
    currentTab: 'tasks' | 'mentions' | 'approvals';
    onTabChange: (tab: 'tasks' | 'mentions' | 'approvals') => void;
    tasksBadge?: number;
    mentionsBadge?: number;
    approvalsBadge?: number;
  };
  /** Optional custom slot rendered at the start of the filter bar row (e.g. accounting period selector). */
  filterBarCustomSlot?: React.ReactNode;
  availableTags?: string[]; // Tags for tag filter
  onItemClick?: (item: WorkItem) => void; // Custom item click handler (overrides default modal)
  enableTimeAllocation?: boolean; // Show Time Allocation tab (default: false, enable for operations/scheduling modules)
  onColumnsExposed?: (columns: Array<{ key: string; label: string; color: string; description?: string }>) => void; // Callback to expose current columns
  preferenceReloadKey?: number; // Increment to force preference reload after modal saves
  refreshNonce?: number; // Increment to trigger a data refresh without remounting (preserves scroll/filters/selection)
  /** Default sort applied on mount and when the user has not selected a sort.
   *  When omitted, defaults to [{ field: 'created_at', direction: 'desc' }]. */
  defaultSort?: Array<{ field: string; direction: 'asc' | 'desc' }>;
  // Header configuration
  showHeader?: boolean; // Show enterprise header (default: true)
  headerTitle?: string; // Header title (e.g., "Service Jobs", "Projects")
  headerSubtitle?: string; // Header subtitle/description
  headerIcon?: React.ElementType; // Header icon component
  headerActions?: React.ReactNode; // Custom action buttons/content in header (e.g., view toggles, create buttons)
  hideHeaderActions?: boolean; // Hide any action buttons in header
  moduleHeaderProps?: Partial<ModuleHeaderProps>; // Optional full ModuleHeader override for parent-owned shells
  /** Alternative to moduleHeaderProps: renders a StrictModuleHeader instead.
   *  When provided, WorkItemsViewWrapper merges its computed stat cards and
   *  status filter content into this object and passes it to ModulePageShell. */
  strictHeaderProps?: StrictModuleHeaderProps | null;
  /** Content rendered at the top of the filter bar, between the header and
   *  the status filter tabs / search bar. Use for banners like intake email
   *  slots that must sit below the header but above the filter controls. */
  filterBarPrefix?: React.ReactNode;
  /** Favourites star for ModuleHeader — applied after moduleHeaderProps merge (use when shell must not lose this, e.g. DynamicOperationalView). */
  headerFavouriteConfig?: ModuleHeaderProps['favouriteConfig'];
  /** External doc-type filter driven by parent (e.g. BillingSubmissionQueue quote/proforma/invoice tabs). */
  activeDocTypeFilter?: string | null;
  onActiveDocTypeFilterChange?: (filter: string | null) => void;
  // DataGrid options
  financeMode?: boolean; // Show finance columns (Amount, Balance, Issued Date) instead of standard columns
  /** When financeMode, narrows specialised columns (e.g. invoice batch / recurring). */
  financeListDocumentType?: string;
  /** Quick filter chip: recurring-linked invoices only. */
  showFinanceRecurringQuickFilter?: boolean;
  showContactAvatar?: boolean; // Show contact avatar/initials in contact column
  dataGridStyleVariant?: WorkItemsDataGridStyleVariant; // Visual surface variant for the shared data grid
  /**
   * Inventory entity column preset. When set, the data grid uses an
   * inventory-specific column layout instead of the standard/finance columns.
   * Auto-derived from `workItemType` when `workItemType` is one of:
   * 'stock_transfer' | 'stock_reservation' | 'approval_request' | 'price_list'
   * | 'stock_count' | 'stock_alert' | 'maintenance' | 'warehouse' | 'asset'.
   * Can also be set explicitly to override.
   */
  inventoryMode?: string;
  // Status filter tabs (for finance documents - shows tabs like All, Draft, Sent, Paid, etc.)
  statusFilterOptions?: Array<{
    value: string;
    label: string;
    color?: string;
    icon?: React.ElementType;
  }>;
  /**
   * Override the status options shown in the WorkItemsFilterBar dropdown.
   * When provided, these replace the workflow-derived status options so
   * finance document types can show their own status set (Draft/Sent/Paid)
   * in the filter bar instead of the header pill row.
   */
  filterBarStatusOptionsOverride?: Array<{
    value: string;
    label: string;
    color?: string;
    icon?: React.ElementType;
  }>;
  embeddedStatusTabs?: boolean;
  initialStatusFilter?: string | null;
  onStatusFilterChange?: (status: string | null) => void; // Callback when status tab changes
  // Stats cards (for finance mode - shows KPI cards like Total Value, Outstanding, etc.)
  // Can be a static array OR a function that receives items to compute stats dynamically
  statsCards?: Array<{
    label: string;
    value: string;
    sublabel?: string;
    icon?: React.ElementType;
    highlight?: boolean; // Blue highlight
    warning?: boolean; // Red/orange warning highlight
  }> | ((items: WorkItem[]) => Array<{
    label: string;
    value: string;
    sublabel?: string;
    icon?: React.ElementType;
    highlight?: boolean;
    warning?: boolean;
  }>);
  /** Publishes the resolved stat cards as StrictStatCard[] so a parent
   *  StrictModuleHeader can host them when `showHeader={false}` suppresses
   *  this wrapper's own header (embedded custom views). */
  onStatsChange?: (cards: StrictStatCard[]) => void;
  // Custom actions for finance mode
  onCreateItem?: () => void; // Custom create handler
  createButtonLabel?: string; // Custom create button label (e.g., "New Quote")
  createButtonDataTourId?: string; // Data tour ID for the create button
  // Contact filter (December 2025)
  showContactFilter?: boolean; // Show contact search filter in filter bar
  contactFilterTypes?: string[]; // Filter contacts by type (e.g., ['customer', 'supplier'])
  // Header color override (February 2026)
  headerColor?: string; // Override the header/primary color (e.g., from useModuleColor)
  // Module access key (January 2026)
  moduleKey?: string; // Explicit module key for module_access user lookup (e.g., 'total-operations', 'total-finance')
  // If not provided, will be derived from moduleContext/templateKey using a mapping
  // Kanban styling options (January 2026)
  kanbanStylePreset?: string; // Preset ID from KanbanStylePresets (e.g., 'classic', 'vibrant', 'minimal', 'glass', 'darkPro', 'neon', 'pastel', 'corporate', 'jira', 'gradientCards')
  kanbanColumnColors?: Record<string, { // Override column colors for specific statuses
    headerBackground?: string;
    headerTextColor?: string;
    border?: string;
    countBadgeBackground?: string;
  }>;
  // Calendar view options
  hideScheduleButton?: boolean; // Hide the Schedule View button in calendar (for non-scheduling modules like email tasks)
  // Active item type filter from parent (February 2026)
  // When parent (DynamicOperationalView) manages item type filtering and fetches multiple types,
  // this tells the wrapper which type is "primary" for filtering non-schedule views (list, board/assignment, tree, gantt).
  // The schedule/time_allocation view always shows all items regardless of this filter.
  activeItemTypeFilter?: string | null;
  // Override for the "Show deleted" filter / restore actions. When provided,
  // the parent decides visibility (e.g. the `delete` action-permission matrix
  // cell from PermissionsSlideOver). When omitted, falls back to the legacy
  // owner/root role check.
  canViewDeletedItems?: boolean;
  // Default board view mode — controls which KanbanBoard internal view to start with (February 2026)
  // Useful when parent component provides its own view toggle (Status Board, Assign Board, Schedule, etc.)
  defaultBoardViewMode?: 'status' | 'assignment' | 'time_allocation' | 'resource_schedule';
  // Hide KanbanBoard's internal view mode toggle (when parent provides its own)
  hideBoardViewToggle?: boolean;
  // NEW: Callback fired when an appointment is created/updated via schedule drag-drop (February 2026)
  onAppointmentChange?: (event: { action: 'created' | 'updated' | 'moved'; appointmentId?: string; date: string; assignedTo?: number }) => void;
  // NEW: Navigate to route planning view from schedule views (February 2026)
  onNavigateToRoutes?: (date: string) => void;
  /** Extra action buttons per row in list view (e.g. manufacturing → BOM drawings for a job). */
  renderListRowExtraActions?: (item: DataGridWorkItem) => React.ReactNode;
  /** Optional swipe-to-reveal actions for mobile list cards. */
  swipeActions?: (item: DataGridWorkItem) => { leading?: import('@/components/SwipeableListItem').SwipeAction[]; trailing?: import('@/components/SwipeableListItem').SwipeAction[] } | null;
  /** Optional thumbnail rendered before the item content in both mobile cards and desktop rows. */
  renderRowThumbnail?: (item: DataGridWorkItem) => React.ReactNode;
  // Active view label — shown as heading in the KanbanBoard toolbar (e.g. "Jobs — Status Board")
  activeViewLabel?: string;
  // Opens the shared board design preferences modal from board-owned title rows
  onOpenBoardPreferences?: () => void;
  // Hide the internal board toolbar when the parent header renders those controls
  hideBoardToolbar?: boolean;
  // Publish board toolbar state upward to a parent header
  onBoardToolbarChange?: (toolbar: BoardToolbarModel | null) => void;
  // Preferred tab when opening the detail modal from this view
  detailInitialTab?: WorkItemTabId;
  /** Curated tab set for the detail modal opened from this view (e.g. lean visit detail). */
  detailVisibleTabs?: WorkItemTabId[];
  /** Override the data-tour-id on the detail modal container (defaults to "work-item-detail-modal"). */
  detailModalTourId?: string;
  // External stat filter driven by parent headers/cards
  activeHeaderStatFilter?: WorkItemStatFilter | null;
  onActiveHeaderStatFilterChange?: (filter: WorkItemStatFilter | null) => void;
  // External date range from parent (e.g. customer portal time range filter)
  // When provided, overrides the filter bar's dateRange for API calls
  externalDateRange?: {
    field: 'created_at' | 'updated_at' | 'due_date' | 'start_date';
    start?: Date;
    end?: Date;
  };
  flushContent?: boolean;
  /** Custom React node rendered in the BulkActionsBar (e.g. finance bulk promote/approve buttons). */
  bulkCustomActions?: React.ReactNode;
  /** Per-button visibility overrides for the standard BulkActionsBar actions.
   *  Omitted keys default to shown (subject to readOnly/permissions). Use this
   *  to hide work-item actions that don't apply to the view (e.g. finance DIN
   *  processing where assign/status/priority/tags/due-date are irrelevant). */
  bulkActionVisibility?: {
    assign?: boolean;
    status?: boolean;
    priority?: boolean;
    tags?: boolean;
    dueDate?: boolean;
    delete?: boolean;
    close?: boolean;
    export?: boolean;
  };
  /** Callback fired when the bulk selection changes (selected item IDs). */
  onSelectionChange?: (selectedItems: Set<string>) => void;
  /** Callback fired when the filtered/visible items change, so parents can
   *  cache them and avoid redundant refetches (e.g. preflight checks). */
  onItemsLoaded?: (items: WorkItem[]) => void;
  /** Optional custom empty state override for the data grid (title, description, icon, CTA). */
  emptyStateConfig?: {
    title: string;
    description: string;
    icon?: React.ElementType;
    ctaLabel?: string;
    onCtaClick?: () => void;
  };
}

const STATUS_COLOR_CLASS_MAP: Record<string, string> = {
  slate: 'bg-slate-500',
  gray: 'bg-gray-500',
  zinc: 'bg-zinc-500',
  neutral: 'bg-neutral-500',
  stone: 'bg-stone-500',
  red: 'bg-red-500',
  orange: 'bg-orange-500',
  amber: 'bg-amber-500',
  yellow: 'bg-yellow-500',
  lime: 'bg-lime-500',
  green: 'bg-green-500',
  emerald: 'bg-emerald-500',
  teal: 'bg-teal-500',
  cyan: 'bg-cyan-500',
  sky: 'bg-sky-500',
  blue: 'bg-blue-500',
  indigo: 'bg-indigo-500',
  violet: 'bg-violet-500',
  purple: 'bg-purple-500',
  fuchsia: 'bg-fuchsia-500',
  pink: 'bg-pink-500',
  rose: 'bg-rose-500',
};

function getStatusDotClass(color?: string) {
  return STATUS_COLOR_CLASS_MAP[color || 'gray'] || 'bg-gray-500';
}

function ensureAppointmentWorkflowStates<T extends { key: string; isSystem?: boolean }>(statuses: T[], itemType?: string | null): T[] {
  if (itemType !== 'appointment') {
    return statuses;
  }

  const mergedStates = [...statuses];
  const requiredStates = [
    { key: 'ai_review', label: 'AI Review', color: 'purple', description: 'Flagged by AI for manager review', isSystem: true },
    { key: 'ai_on_hold', label: 'AI On Hold', color: 'amber', description: 'Held in the AI review workflow pending action', isSystem: true },
  ] as unknown as T[];
  const terminalStateKeys = new Set(['resolved', 'closed', 'completed', 'missed', 'no_show', 'cancelled']);

  requiredStates.forEach((requiredState) => {
    if (mergedStates.some(state => state.key === requiredState.key)) {
      return;
    }

    const insertIndex = mergedStates.findIndex(state => terminalStateKeys.has(state.key));
    mergedStates.splice(insertIndex >= 0 ? insertIndex : mergedStates.length, 0, requiredState);
  });

  return mergedStates;
}

const DEFAULT_TREE_VISIBLE_COLUMNS: TreeVisibleColumnKey[] = ['type', 'assignees', 'progress', 'status', 'actions'];
const TREE_VISIBLE_COLUMN_LABELS: Record<TreeVisibleColumnKey, string> = {
  type: 'Type',
  assignees: 'Assigned',
  progress: 'Progress',
  status: 'Status',
  actions: 'Actions',
};

const noopToolbarAction = () => { };

function sanitizeTreeVisibleColumns(value: unknown): TreeVisibleColumnKey[] {
  if (!Array.isArray(value)) {
    return [...DEFAULT_TREE_VISIBLE_COLUMNS];
  }

  const allowed = new Set<TreeVisibleColumnKey>(DEFAULT_TREE_VISIBLE_COLUMNS);
  const sanitized = value.filter((entry): entry is TreeVisibleColumnKey => typeof entry === 'string' && allowed.has(entry as TreeVisibleColumnKey));

  return sanitized.length > 0 ? Array.from(new Set(sanitized)) : [...DEFAULT_TREE_VISIBLE_COLUMNS];
}

function buildBaseToolbarModel(): BoardToolbarModel {
  return {
    showZoomControls: false,
    zoomLabel: '100%',
    canZoomIn: false,
    canZoomOut: false,
    onZoomIn: noopToolbarAction,
    onZoomOut: noopToolbarAction,
    onZoomReset: noopToolbarAction,
    showDateControls: false,
    showTimeWindowControls: false,
    showSidebarToggle: false,
    showColumnSettings: false,
    columnSettingsOpen: false,
    visibleColumnCount: 0,
    totalColumnCount: 0,
    columnOptions: [],
  };
}

function getBoardToolbarModelSignature(toolbar: BoardToolbarModel | null): string {
  if (!toolbar) {
    return 'null';
  }

  return JSON.stringify({
    showZoomControls: toolbar.showZoomControls,
    zoomLabel: toolbar.zoomLabel,
    canZoomIn: toolbar.canZoomIn,
    canZoomOut: toolbar.canZoomOut,
    showDateControls: toolbar.showDateControls,
    selectedDateLabel: toolbar.selectedDateLabel,
    selectedDateCompactLabel: toolbar.selectedDateCompactLabel,
    isToday: toolbar.isToday,
    showTimeWindowControls: toolbar.showTimeWindowControls,
    canRollEarlier: toolbar.canRollEarlier,
    canRollLater: toolbar.canRollLater,
    canResetTimeWindow: toolbar.canResetTimeWindow,
    showSidebarToggle: toolbar.showSidebarToggle,
    sidebarVisible: toolbar.sidebarVisible,
    sidebarCount: toolbar.sidebarCount,
    sidebarLabel: toolbar.sidebarLabel,
    showColumnSettings: toolbar.showColumnSettings,
    columnSettingsOpen: toolbar.columnSettingsOpen,
    visibleColumnCount: toolbar.visibleColumnCount,
    totalColumnCount: toolbar.totalColumnCount,
    columnOptions: toolbar.columnOptions.map((column) => ({
      key: column.key,
      label: column.label,
      count: column.count,
      visible: column.visible,
      disabled: column.disabled,
      colorClassName: column.colorClassName,
    })),
  });
}

function hasNonEmptyGridColumnValue(item: DataGridWorkItem, columnKey: string): boolean {
  switch (columnKey) {
    case 'item_number':
    case 'title':
    case 'priority':
    case 'status':
    case 'item_type':
    case 'created_at':
    case 'actions':
      return true;
    case 'assignees':
      return Boolean((item.assignees && item.assignees.length > 0) || item.assigned_team);
    default: {
      const value = (item as unknown as Record<string, unknown>)[columnKey];
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== null && value !== undefined && String(value).trim().length > 0;
    }
  }
}

// ============================================================================
// COMPONENT
// ============================================================================

export default function WorkItemsViewWrapper({
  activeTab,
  workItemType,
  workItemSubtype,
  templateKey,
  moduleContext,
  teamId,
  solutionKey,
  workspaceId,
  workflowStates,
  hideViewToggle = false,
  readOnly = false,
  viewToggleInHeader = false,
  defaultViewMode = 'list',
  enabledViewModes,
  enableSlaView = false,
  hideFilters = false,
  filterByAssignedUser = false,
  assignedOnly = false,
  personalMode = false,
  assignedUserScope,
  additionalFilters,
  includePlatformSubscriptionInvoices = false,
  permissions = { can_view: true, can_create: true, can_edit: true, can_delete: true, can_admin: false },
  enableItemTypeFilter = false,
  showAllFilter = true,
  autoLoadTypeFilters = false, // NEW: Auto-load type filters from config (January 2026)
  itemTypeOptions: propItemTypeOptions = [],
  customFilterButtons = [],
  showCreateDropdown = false,
  onItemTypeSelect, // Callback when user selects item type from create dropdown
  workflowField = 'status', // Default to status field
  boardKey,
  statusFilter, // Optional filter for invoice status
  excludeStatuses, // Optional filter to hide certain statuses
  // NEW: Filter bar options (December 2025)
  showFilterBar = true, // Show the comprehensive filter bar by default
  filterBarCompact = false,
  filterBarSingleRow = false,
  showFilterBarStats = true,
  showFilterBarQuickFilters = true,
  showFilterBarPresets = true,
  showFilterBarStatusFilter = true,
  showFilterBarPriorityFilter = true,
  showFilterBarAssigneeFilter = true,
  showFilterBarTypeFilter = true,
  showFilterBarDateFilter = true,
  showFilterBarSortControls = true,
  availableAssignees = [],
  hideAssigneeSelectAll = false,
  attentionCount,
  onAttentionCountClick,
  mobileTabToggle,
  filterBarCustomSlot,
  availableTags = [],
  onItemClick, // Custom item click handler
  enableTimeAllocation = false, // Default to false - only show for operations/scheduling modules
  onColumnsExposed, // Callback to expose current columns
  preferenceReloadKey = 0, // Increment to force preference reload after modal saves
  refreshNonce = 0, // Increment to trigger a data refresh without remounting
  // Header props
  showHeader = true,
  headerTitle,
  headerSubtitle,
  headerIcon,
  headerActions,
  moduleHeaderProps,
  strictHeaderProps,
  filterBarPrefix,
  headerFavouriteConfig,
  activeDocTypeFilter = null,
  onActiveDocTypeFilterChange,
  // DataGrid options
  financeMode = false,
  financeListDocumentType,
  showFinanceRecurringQuickFilter = false,
  showContactAvatar = false,
  dataGridStyleVariant = 'default',
  inventoryMode,
  // Status filter tabs
  statusFilterOptions = [],
  filterBarStatusOptionsOverride,
  embeddedStatusTabs = false,
  initialStatusFilter = null,
  onStatusFilterChange,
  // Stats cards
  statsCards = [],
  onStatsChange,
  // Custom actions
  onCreateItem,
  createButtonLabel = 'New Item',
  createButtonDataTourId,
  // Contact filter - enabled by default for all views
  showContactFilter = true,
  contactFilterTypes,
  // Header color override (February 2026)
  headerColor,
  // Module access key (January 2026)
  moduleKey: propModuleKey,
  // Kanban styling options (January 2026)
  kanbanStylePreset: propKanbanStylePreset, // If not provided, will load from client config
  kanbanColumnColors,
  // Active item type filter from parent (February 2026)
  activeItemTypeFilter: parentActiveItemTypeFilter = null,
  // Optional "Show deleted" visibility override (e.g. ops `delete` matrix cell)
  canViewDeletedItems: canViewDeletedItemsProp,
  // View mode label from parent (shown in KanbanBoard toolbar heading)
  activeViewLabel: propActiveViewLabel,
  onOpenBoardPreferences,
  hideBoardToolbar = false,
  onBoardToolbarChange,
  detailInitialTab,
  detailVisibleTabs,
  detailModalTourId,
  activeHeaderStatFilter = null,
  onActiveHeaderStatFilterChange,
  // Default board view mode (February 2026)
  defaultBoardViewMode,
  // Hide board's internal view toggle (February 2026)
  hideBoardViewToggle = false,
  // Appointment change callback (February 2026)
  onAppointmentChange,
  // Navigate to route planning (February 2026)
  onNavigateToRoutes,
  onConfigureSla,
  renderListRowExtraActions,
  swipeActions,
  renderRowThumbnail,
  externalDateRange,
  flushContent,
  bulkCustomActions,
  bulkActionVisibility,
  onSelectionChange,
  onItemsLoaded,
  emptyStateConfig,
  defaultSort: defaultSortProp,
}: WorkItemsViewWrapperProps) {
  const { clientBranding } = useTheme();
  const { isMobile } = useResponsive();
  const workItemOpener = useOptionalWorkItemOpener();

  // Open team member profile modal from avatar click
  const openTeamMember = useOpenTeamMember();

  // NEW: Load client's kanban style preference if no explicit prop (January 2026)
  const { style: clientKanbanStyle } = useKanbanStylePreference();
  const kanbanStylePreset = propKanbanStylePreset || clientKanbanStyle;

  // Resolve the module key for module_access user lookup
  // Template keys like 'service-operations' need to map to module keys like 'total-operations'
  const resolvedModuleKey = useMemo(() => {
    // If explicit moduleKey prop is provided, use it
    if (propModuleKey) return propModuleKey;

    // If moduleContext already looks like a module key (starts with 'total-'), use it
    if (moduleContext?.startsWith('total-')) return moduleContext;

    // Map common template/context keys to their parent module keys
    // These are operational templates that belong to total-operations
    const OPERATIONS_TEMPLATES = [
      'service-operations', 'fleet-management', 'logistics-waste', 'manufacturing',
      'forestry-operations', 'construction-management', 'facility-management',
      'healthcare-operations', 'hospitality-operations', 'agriculture-farming',
      'automotive-services', 'cleaning-services', 'events-entertainment',
      'energy-utilities', 'asset-inventory-management', 'property-management',
      'software-development', 'general-operations', 'asset-maintenance',
      'field-services', 'email-task-management', 'drawings-manager', 'compliance-manager'
    ];

    const ACCOUNTS_TEMPLATES = ['financial-management', 'invoicing', 'billing'];
    const SUPPORT_TEMPLATES = ['support-desk', 'helpdesk', 'customer-support'];
    const CRM_TEMPLATES = ['crm-management', 'sales', 'lead-management'];

    const contextOrTemplate = moduleContext || templateKey;

    if (OPERATIONS_TEMPLATES.includes(contextOrTemplate || '')) return 'total-operations';
    if (ACCOUNTS_TEMPLATES.includes(contextOrTemplate || '')) return 'total-finance';
    if (SUPPORT_TEMPLATES.includes(contextOrTemplate || '')) return 'total-support';
    if (CRM_TEMPLATES.includes(contextOrTemplate || '')) return 'total-crm';

    // Default to total-operations if no mapping found
    return 'total-operations';
  }, [propModuleKey, moduleContext, templateKey]);

  useEffect(() => {
    void logUiAudit({
      component: 'WorkItemsViewWrapper',
      action: 'mount',
      moduleKey: resolvedModuleKey,
      details: `Work items view mounted (${templateKey || moduleContext || 'default'})`,
      metadata: {
        templateKey,
        moduleContext,
        financeMode,
        financeListDocumentType,
      },
    });
  }, [resolvedModuleKey, templateKey, moduleContext, financeMode, financeListDocumentType]);

  // NEW: Auto-load type filter options from work_item_number_sequences config (January 2026)
  const { typeFilterOptions: autoLoadedTypeFilters } = useTypeFilterConfig(moduleContext);

  // Fetch centralized work item type metadata for consistent labels, colors, and icons
  const { types: workItemTypes, getTypeMeta: getWorkItemTypeMeta } = useWorkItemTypes();

  // Prime the typeIdentity cache so getWorkItemTypeIdentity() returns real metadata
  // for types not covered by its hardcoded switch
  useEffect(() => {
    if (workItemTypes.length > 0) {
      primeTypeIdentityCache(
        workItemTypes.map((t) => ({
          type_key: t.type_key,
          type_label: t.type_label,
          color: t.color,
          icon: t.icon,
        }))
      );
    }
  }, [workItemTypes]);

  // NEW: Auto-load workflow statuses from work_item_number_sequences config (January 2026)
  // Parse workItemType to get the primary type for status lookup
  const requestedItemTypes = useMemo(() => {
    if (!workItemType) return [];
    return workItemType.split(',').map(t => t.trim()).filter(Boolean);
  }, [workItemType]);

  const primaryItemType = useMemo(() => {
    if (requestedItemTypes.length === 0) return undefined;
    // Priority: job > project > task > first type
    return ['job', 'project', 'task'].find(t => requestedItemTypes.includes(t)) || requestedItemTypes[0];
  }, [requestedItemTypes]);

  // Merge prop options with auto-loaded options (prop takes precedence if both provided)
  const itemTypeOptions = useMemo(() => {
    if (autoLoadTypeFilters && propItemTypeOptions.length === 0) {
      // Use auto-loaded options when no prop options provided
      return autoLoadedTypeFilters;
    }
    // Use prop options (for backward compatibility)
    return propItemTypeOptions;
  }, [autoLoadTypeFilters, propItemTypeOptions, autoLoadedTypeFilters]);

  // Memoize primary color to prevent infinite re-renders
  // headerColor prop takes priority when provided (e.g., from useModuleColor)
  const primaryColor = useMemo(() => {
    return headerColor || clientBranding?.primary_color || '#10b981';
  }, [headerColor, clientBranding?.primary_color]);
  const [isNetworkOnline, setIsNetworkOnline] = useState(() => networkState.isOnline());
  const [wsConnectionStatus, setWsConnectionStatus] = useState<ConnectionStatus>(() => wsClient.getStatus());
  const [selectedItem, setSelectedItem] = useState<WorkItem | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>(defaultViewMode);
  const [kanbanToolbarModel, setKanbanToolbarModel] = useState<BoardToolbarModel | null>(null);
  const [selectedItemTypes, setSelectedItemTypes] = useState<string[]>([]);
  // "Agent" pill — restrict to items the GavShell agent has touched
  // (custom_fields.agent present). Orthogonal to item_type filters.
  const [agentOnly, setAgentOnly] = useState(false);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<string | null>(initialStatusFilter && initialStatusFilter !== 'all' ? initialStatusFilter : null); // For status filter tabs
  const [deletedFilterMode, setDeletedFilterMode] = useState<'active' | 'deleted' | 'all'>('active');
  const [, setLoadingUsers] = useState(false);
  const [fetchedUsers, setFetchedUsers] = useState<WorkItemAssigneeOption[]>([]);
  const [teamScopedKnownUserIds, setTeamScopedKnownUserIds] = useState<number[] | null>(null);
  const [selectedContact, setSelectedContact] = useState<ContactOption | null>(null); // Contact filter
  const [debouncedWorkItemsSearch, setDebouncedWorkItemsSearch] = useState('');
  // Debounced filter state for frontend-first + async server-side filtering
  const [debouncedFilters, setDebouncedFilters] = useState({
    status: [] as string[],
    priority: [] as string[],
    assignedTo: [] as (number | null)[],
    tags: [] as string[],
    dateRange: undefined as FilterConfig['dateRange'],
  });
  const [serverStatusCounts, setServerStatusCounts] = useState<Record<string, number> | null>(null);
  const [debouncedSort, setDebouncedSort] = useState<{ field: string; direction: 'asc' | 'desc' } | null>(null);
  const [dataMutationRefreshToken, setDataMutationRefreshToken] = useState(0);
  const [listGridPage, setListGridPage] = useState(1);
  const [listGridPageSize, setListGridPageSize] = useState(50);
  /** Page N≥2 keyset cursor (after_created_at + after_id) when walking sequentially; offset used if missing (e.g. jump). */
  const [listPageCursors, setListPageCursors] = useState<Record<number, { created_at: string; id: string }>>({});
  /** Selected date from the schedule view (vertical/horizontal), lifted via onScheduleDateChange.
   *  Used to build a date-windowed API query so the schedule only fetches items near the visible date
   *  instead of loading all records and hoping the relevant ones fall within the 200-item limit. */
  const [scheduleSelectedDate, setScheduleSelectedDate] = useState<Date | null>(null);

  const focusedWorkflowItemType = useMemo(() => {
    if (parentActiveItemTypeFilter) return parentActiveItemTypeFilter;
    if (selectedItemTypes.length === 1) return selectedItemTypes[0];
    return primaryItemType;
  }, [parentActiveItemTypeFilter, selectedItemTypes, primaryItemType]);

  const activeTypeIdentity = useMemo(
    () => getWorkItemTypeIdentity(focusedWorkflowItemType, primaryColor),
    [focusedWorkflowItemType, primaryColor]
  );

  const activeItemTypeLabel = useMemo(() => {
    if (!focusedWorkflowItemType) {
      return undefined;
    }

    const typeMeta = getWorkItemTypeMeta(focusedWorkflowItemType);
    return itemTypeOptions.find((option) => option.id === focusedWorkflowItemType)?.label
      || typeMeta?.type_label
      || typeMeta?.type_label_plural
      || activeTypeIdentity.label;
  }, [activeTypeIdentity.label, focusedWorkflowItemType, getWorkItemTypeMeta, itemTypeOptions]);

  const listViewPersistItemType = useMemo(() => {
    return focusedWorkflowItemType
      || primaryItemType
      || requestedItemTypes[0]
      || workItemType?.split(',').map((type) => type.trim()).find(Boolean)
      || null;
  }, [focusedWorkflowItemType, primaryItemType, requestedItemTypes, workItemType]);

  const legacyListViewBoardKey = useMemo(() => {
    const rawScope = [
      templateKey || moduleContext || activeTab || 'work-items',
      listViewPersistItemType || 'mixed',
      financeMode ? 'finance' : 'standard',
      dataGridStyleVariant,
    ]
      .filter(Boolean)
      .map((value) => String(value).trim().replace(/[^a-zA-Z0-9_-]+/g, '-'));

    return `work-items-grid__${rawScope.join('__')}`;
  }, [activeTab, dataGridStyleVariant, financeMode, listViewPersistItemType, moduleContext, templateKey]);
  const listViewBoardKey = useMemo(() => buildWorkItemsViewPreferenceScopeKey({
    templateKey: templateKey || activeTab,
    moduleContext,
    boardKey,
    itemType: listViewPersistItemType,
    financeMode,
    dataGridStyleVariant,
  }), [activeTab, boardKey, dataGridStyleVariant, financeMode, listViewPersistItemType, moduleContext, templateKey]);

  const listViewPreferenceKey = useMemo(() => `work-items-view:list-columns:${listViewBoardKey}`, [listViewBoardKey]);
  const treeViewPreferenceKey = useMemo(() => `work-items-view:tree-columns:${listViewBoardKey}`, [listViewBoardKey]);
  const legacyListViewPreferenceKey = useMemo(() => `work-items-view:list-columns:${legacyListViewBoardKey}`, [legacyListViewBoardKey]);
  const legacyTreeViewPreferenceKey = useMemo(() => `work-items-view:tree-columns:${legacyListViewBoardKey}`, [legacyListViewBoardKey]);
  // Derive the effective inventory column preset. When `inventoryMode` is
  // explicitly set, it wins. Otherwise, auto-derive from `workItemType` when
  // it matches one of the inventory entity types.
  const effectiveInventoryMode = useMemo(() => {
    if (inventoryMode) return inventoryMode;
    const INVENTORY_WORK_ITEM_TYPES = new Set([
      'stock_transfer',
      'stock_reservation',
      'approval_request',
      'price_list',
      'stock_count',
      'stock_alert',
      'maintenance',
      'warehouse',
      'asset',
    ]);
    if (workItemType && INVENTORY_WORK_ITEM_TYPES.has(workItemType)) {
      return workItemType;
    }
    return undefined;
  }, [inventoryMode, workItemType]);

  const gridColumnDefinitions = useMemo<WorkItemsDataGridColumnDef[]>(() => getWorkItemsDataGridColumns({
    financeMode,
    financeDocumentType: financeListDocumentType,
    showDeletedFilter: true,
    styleVariant: dataGridStyleVariant,
    inventoryMode: effectiveInventoryMode,
  }), [dataGridStyleVariant, effectiveInventoryMode, financeListDocumentType, financeMode]);
  const exportColumnDefs = useMemo<ExportColumnDef[]>(() => {
    const skipKeys = new Set(['select', 'actions']);
    return gridColumnDefinitions
      .filter((col) => !skipKeys.has(col.key))
      .map((col) => ({ key: col.key, label: col.label }));
  }, [gridColumnDefinitions]);
  const gridToggleableColumns = useMemo(() => {
    const toggleableKeys = new Set(getWorkItemsDataGridToggleableColumnKeys(gridColumnDefinitions));
    return gridColumnDefinitions.filter((column) => toggleableKeys.has(column.key));
  }, [gridColumnDefinitions]);
  const defaultListVisibleColumns = useMemo(() => {
    // Respect the `defaultVisible: false` flag on column definitions so
    // optional columns (e.g. Contact Email, PO Ref) are hidden by default
    // while core columns (Title, Status, Amount, etc.) are shown.
    const defaultKeys = gridColumnDefinitions
      .filter((column) =>
        !NON_TOGGLEABLE_WORK_ITEMS_GRID_COLUMN_KEYS.has(column.key) &&
        column.defaultVisible !== false &&
        column.key !== 'deleted_at' &&
        column.key !== 'deleted_by_name'
      )
      .map((column) => column.key);

    if (dataGridStyleVariant === 'fleet') {
      const preferredOrder = ['item_number', 'title', 'status', 'driver', 'contact_name', 'created_at'];
      return preferredOrder.filter((key) => defaultKeys.includes(key));
    }

    return defaultKeys;
  }, [dataGridStyleVariant, gridColumnDefinitions]);
  const [persistedListVisibleColumns, setPersistedListVisibleColumns] = useState<string[]>(defaultListVisibleColumns);
  const [persistedTreeVisibleColumns, setPersistedTreeVisibleColumns] = useState<TreeVisibleColumnKey[]>(DEFAULT_TREE_VISIBLE_COLUMNS);
  const [viewPreferencesLoaded, setViewPreferencesLoaded] = useState(false);

  const getPreferenceRequestHeaders = useCallback(() => {
    const token = authService.getTokens()?.access_token;
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }, []);

  const persistUserPreference = useCallback(async (key: string, value: unknown) => {
    const response = await fetch('/api/v1/preferences', {
      method: 'PATCH',
      headers: getPreferenceRequestHeaders(),
      body: JSON.stringify({ key, value }),
    });

    if (!response.ok) {
      throw new Error(`Failed to persist preference '${key}'`);
    }
  }, [getPreferenceRequestHeaders]);

  useEffect(() => {
    let isCancelled = false;

    const loadViewPreferences = async () => {
      setViewPreferencesLoaded(false);

      try {
        const response = await fetch('/api/v1/preferences', {
          headers: getPreferenceRequestHeaders(),
        });

        if (!response.ok) {
          throw new Error(`Failed to load view preferences (${response.status})`);
        }

        const data = await response.json();
        const preferences = (data?.preferences && typeof data.preferences === 'object') ? data.preferences as Record<string, unknown> : {};

        let resolvedListColumns = preferences[listViewPreferenceKey];
        let resolvedTreeColumns = preferences[treeViewPreferenceKey];
        let migrateLegacyGridColumns = false;

        if (resolvedListColumns === undefined && legacyListViewPreferenceKey !== listViewPreferenceKey) {
          resolvedListColumns = preferences[legacyListViewPreferenceKey];
        }

        if (resolvedTreeColumns === undefined && legacyTreeViewPreferenceKey !== treeViewPreferenceKey) {
          resolvedTreeColumns = preferences[legacyTreeViewPreferenceKey];
        }

        if (!Array.isArray(resolvedListColumns) && typeof window !== 'undefined') {
          const legacyStoredColumns = window.localStorage.getItem(`ta-work-items-grid-visible-columns:${legacyListViewBoardKey}`);
          if (legacyStoredColumns) {
            try {
              const parsedLegacyColumns = JSON.parse(legacyStoredColumns);
              if (Array.isArray(parsedLegacyColumns)) {
                resolvedListColumns = parsedLegacyColumns;
                migrateLegacyGridColumns = true;
              }
            } catch (error) {
              console.warn('[WorkItemsViewWrapper] Failed to parse legacy grid column preferences:', error);
            }
          }
        }

        const gridAllowedColumnKeys = getWorkItemsDataGridToggleableColumnKeys(gridColumnDefinitions);
        const sanitizedListColumns = sanitizeWorkItemsDataGridVisibleColumns(
          resolveVisibleColumnsFromStoredPreference(
            resolvedListColumns,
            gridAllowedColumnKeys,
            defaultListVisibleColumns,
          ),
          gridColumnDefinitions,
        );
        const sanitizedTreeColumns = sanitizeTreeVisibleColumns(
          resolveVisibleColumnsFromStoredPreference(
            resolvedTreeColumns,
            DEFAULT_TREE_VISIBLE_COLUMNS,
            DEFAULT_TREE_VISIBLE_COLUMNS,
          ),
        );

        if (!isCancelled) {
          setPersistedListVisibleColumns(sanitizedListColumns);
          setPersistedTreeVisibleColumns(sanitizedTreeColumns);
          setViewPreferencesLoaded(true);
        }

        if (migrateLegacyGridColumns) {
          void persistUserPreference(
            listViewPreferenceKey,
            buildHiddenColumnPreference(sanitizedListColumns, gridAllowedColumnKeys, defaultListVisibleColumns),
          ).catch((error) => {
            console.warn('[WorkItemsViewWrapper] Failed to migrate legacy grid column preferences:', error);
          });
        }
      } catch (error) {
        console.error('[WorkItemsViewWrapper] Failed to load persisted view preferences:', error);
        if (!isCancelled) {
          setPersistedListVisibleColumns(defaultListVisibleColumns);
          setPersistedTreeVisibleColumns(DEFAULT_TREE_VISIBLE_COLUMNS);
          setViewPreferencesLoaded(true);
        }
      }
    };

    void loadViewPreferences();

    return () => {
      isCancelled = true;
    };
  }, [defaultListVisibleColumns, getPreferenceRequestHeaders, gridColumnDefinitions, legacyListViewBoardKey, legacyListViewPreferenceKey, legacyTreeViewPreferenceKey, listViewPreferenceKey, persistUserPreference, treeViewPreferenceKey]);

  const handleListVisibleColumnsChange = useCallback((columnKeys: string[]) => {
    const sanitizedColumns = sanitizeWorkItemsDataGridVisibleColumns(columnKeys, gridColumnDefinitions);
    setPersistedListVisibleColumns(sanitizedColumns);
    const gridAllowedColumnKeys = getWorkItemsDataGridToggleableColumnKeys(gridColumnDefinitions);

    if (viewPreferencesLoaded) {
      void persistUserPreference(
        listViewPreferenceKey,
        buildHiddenColumnPreference(sanitizedColumns, gridAllowedColumnKeys, defaultListVisibleColumns),
      ).catch((error) => {
        console.error('[WorkItemsViewWrapper] Failed to persist list column preferences:', error);
      });
    }
  }, [defaultListVisibleColumns, gridColumnDefinitions, listViewPreferenceKey, persistUserPreference, viewPreferencesLoaded]);

  const handleTreeVisibleColumnsChange = useCallback((columnKeys: TreeVisibleColumnKey[]) => {
    const sanitizedColumns = sanitizeTreeVisibleColumns(columnKeys);
    setPersistedTreeVisibleColumns(sanitizedColumns);

    if (viewPreferencesLoaded) {
      void persistUserPreference(
        treeViewPreferenceKey,
        buildHiddenColumnPreference(sanitizedColumns, DEFAULT_TREE_VISIBLE_COLUMNS, DEFAULT_TREE_VISIBLE_COLUMNS),
      ).catch((error) => {
        console.error('[WorkItemsViewWrapper] Failed to persist tree column preferences:', error);
      });
    }
  }, [persistUserPreference, treeViewPreferenceKey, viewPreferencesLoaded]);

  const workflowStatusScope = useMemo(() => {
    if (parentActiveItemTypeFilter || selectedItemTypes.length === 1) {
      return undefined;
    }
    return requestedItemTypes.length > 0 ? requestedItemTypes : undefined;
  }, [parentActiveItemTypeFilter, selectedItemTypes, requestedItemTypes]);

  const {
    workflowStatuses: loadedWorkflowStatuses,
    hasCustomStatuses,
  } = useWorkflowStatuses(
    focusedWorkflowItemType,
    workflowStatusScope,
    moduleContext,
  );

  // Prefer IndustrySolutionSettings statuses whenever the client has configured them.
  // Otherwise keep honoring explicit template/workspace workflow definitions.
  const effectiveWorkflowStates = useMemo(() => {
    if (hasCustomStatuses) {
      return ensureAppointmentWorkflowStates(loadedWorkflowStatuses, focusedWorkflowItemType);
    }
    if (workflowStates && workflowStates.length > 0) {
      return ensureAppointmentWorkflowStates(workflowStates, focusedWorkflowItemType);
    }
    return ensureAppointmentWorkflowStates(loadedWorkflowStatuses, focusedWorkflowItemType);
  }, [hasCustomStatuses, workflowStates, loadedWorkflowStatuses, focusedWorkflowItemType]);

  const filterBarStatusOptions = useMemo(() => {
    // Finance override: when provided, use the finance-specific status options
    // (e.g., Draft/Sent/Paid) instead of the workflow-derived states.
    if (filterBarStatusOptionsOverride) {
      return filterBarStatusOptionsOverride.map(opt => ({
        value: opt.value,
        label: opt.label,
        color: opt.color,
      }));
    }
    return effectiveWorkflowStates.map(status => ({
      value: status.key,
      label: status.label,
      color: getStatusDotClass(status.color),
    }));
  }, [effectiveWorkflowStates, filterBarStatusOptionsOverride]);

  // BulkActionsBar status options — must align with the filter bar dropdown.
  // When filterBarStatusOptionsOverride is provided (e.g., Expense Inbox's
  // New/Draft/Pending/Completed/Void), use those same options so the floating
  // bulk-action bar's status dropdown matches the filter bar.
  const bulkBarStatusOptions = useMemo(() => {
    if (filterBarStatusOptionsOverride) {
      return filterBarStatusOptionsOverride.map(opt => ({
        key: opt.value,
        label: opt.label,
        color: opt.color,
      }));
    }
    return effectiveWorkflowStates.map(s => ({ key: s.key, label: s.label, color: `bg-${s.color}-500` }));
  }, [effectiveWorkflowStates, filterBarStatusOptionsOverride]);

  // NEW: Bulk Selection State (February 2026)
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());

  // NEW: Type filter order state for drag-and-drop reordering (January 2026)
  const [typeFilterOrder, setTypeFilterOrder] = useState<string[]>([]);

  // Location picker state for CalendarView drag-drop scheduling
  const [pendingCalendarLocationPick, setPendingCalendarLocationPick] = useState<{
    item: WorkItem;
    scheduledStart: string;
    scheduledEnd: string;
    technicianId: number;
  } | null>(null);

  // Sync viewMode with defaultViewMode prop when it changes (for parent-controlled view modes)
  useEffect(() => {
    setViewMode(defaultViewMode);
  }, [defaultViewMode]);

  const isSlaViewEnabled = useMemo(() => {
    return enableSlaView || defaultViewMode === 'sla' || activeTab.endsWith('-sla');
  }, [activeTab, defaultViewMode, enableSlaView]);

  // LocalStorage key for type filter order
  const typeFilterOrderStorageKey = useMemo(() => {
    const key = templateKey || moduleContext || 'global';
    return `type-filter-order-${key}`;
  }, [templateKey, moduleContext]);

  // Issue D4: persist the status filter across navigations so the user
  // returns to the same filter view. Keyed by the board scope so each
  // inbox/list remembers its own filter independently.
  const statusFilterStorageKey = useMemo(
    () => `work-items-view:status-filter:${listViewBoardKey}`,
    [listViewBoardKey],
  );

  // Restore saved status filter on mount (only when no explicit prop override)
  useEffect(() => {
    if (initialStatusFilter && initialStatusFilter !== 'all') return;
    try {
      const saved = localStorage.getItem(statusFilterStorageKey);
      if (saved && saved !== 'all') {
        setSelectedStatusFilter(saved);
      }
    } catch {
      // localStorage unavailable (private mode) — non-fatal
    }
  }, [statusFilterStorageKey, initialStatusFilter]);

  // Persist status filter on change
  useEffect(() => {
    try {
      if (selectedStatusFilter) {
        localStorage.setItem(statusFilterStorageKey, selectedStatusFilter);
      } else {
        localStorage.removeItem(statusFilterStorageKey);
      }
    } catch {
      // non-fatal
    }
  }, [selectedStatusFilter, statusFilterStorageKey]);

  // Load type filter order from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(typeFilterOrderStorageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          setTypeFilterOrder(parsed);
        }
      }
    } catch (error) {
      console.error('[WorkItemsViewWrapper] Error loading type filter order:', error);
    }
  }, [typeFilterOrderStorageKey]);

  // Compute ordered type filter options
  const orderedItemTypeOptions = useMemo(() => {
    if (typeFilterOrder.length === 0) {
      return itemTypeOptions;
    }

    // Sort itemTypeOptions based on saved order
    const ordered = [...itemTypeOptions].sort((a, b) => {
      const indexA = typeFilterOrder.indexOf(a.id);
      const indexB = typeFilterOrder.indexOf(b.id);

      // Items not in order go to the end
      if (indexA === -1 && indexB === -1) return 0;
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;

      return indexA - indexB;
    });

    return ordered;
  }, [itemTypeOptions, typeFilterOrder]);


  // Get current user ID if filtering by assigned user
  // personalMode implies filterByAssignedUser + assignedOnly
  const isPersonalMode = personalMode || (filterByAssignedUser && assignedOnly);
  const effectiveFilterByAssignedUser = personalMode || filterByAssignedUser;
  const effectiveAssignedOnly = personalMode || assignedOnly;

  // Use useAuth for reactive user state — authService.getUser() alone won't
  // update if the user object is loaded after this component mounts (e.g.
  // async storage init on Capacitor, or a fresh login that re-renders the
  // tree). This ensures canViewDeletedItems reflects the current role.
  const { user: authUser } = useAuth();
  const currentUser = authUser as {
    id?: number;
    role?: string;
    isRoot?: boolean;
    permissions?: { role?: string };
  } | null;
  const effectiveRole = currentUser?.permissions?.role || currentUser?.role || 'user';
  const isRoot = effectiveRole === 'root' || currentUser?.isRoot === true || currentUser?.id === 46;
  // When the parent supplies canViewDeletedItems (ops views pass the `delete`
  // action-permission matrix cell), it governs "Show deleted" / restore.
  // Otherwise keep the legacy owner/root role gate.
  const canViewDeletedItems = canViewDeletedItemsProp ?? (isRoot || effectiveRole === 'owner');

  const currentUserId = useMemo(() => {
    if (isPersonalMode || effectiveFilterByAssignedUser) {
      const user = authService.getUser();
      return user?.id;
    }
    return undefined;
  }, [isPersonalMode, effectiveFilterByAssignedUser]);

  useEffect(() => {
    const unsubscribeNetwork = networkState.subscribe((state) => {
      setIsNetworkOnline(state.isOnline);
    });

    const unsubscribeConnection = wsClient.onConnection((status) => {
      setWsConnectionStatus(status);
    });

    setIsNetworkOnline(networkState.isOnline());
    setWsConnectionStatus(wsClient.getStatus());

    return () => {
      unsubscribeNetwork();
      unsubscribeConnection();
    };
  }, []);

  // Fetch users with module access (for assignment board and technician calendar)
  useEffect(() => {
    if (!resolvedModuleKey) {
      setFetchedUsers([]);
      setTeamScopedKnownUserIds(null);
      setLoadingUsers(false);
      return;
    }

    let isCancelled = false;
    const useTeamScopedAssignees = resolvedModuleKey === 'total-operations' && Boolean(teamId);
    const useSolutionScopedAssignees = resolvedModuleKey === 'total-operations' && !useTeamScopedAssignees && Boolean(solutionKey);

    const fetchUsers = async () => {
      setLoadingUsers(true);
      try {
        const token = authService.getTokens()?.access_token;
        const params = new URLSearchParams({ module_key: resolvedModuleKey });
        const headers = {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        };

        if (useSolutionScopedAssignees && solutionKey) {
          params.set('solution_key', solutionKey);
        }

        let includedInactiveTeamMembers = useTeamScopedAssignees;
        let response = await fetch(
          useTeamScopedAssignees
            ? `/api/v1/teams/${teamId}/members?include_inactive=true`
            : useSolutionScopedAssignees
              ? `/api/v1/directory?${params.toString()}`
              : `/api/v1/module-access/users?${params.toString()}`,
          { headers }
        );

        if (!response.ok && useTeamScopedAssignees && response.status === 403) {
          includedInactiveTeamMembers = false;
          response = await fetch(`/api/v1/teams/${teamId}/members`, { headers });
        }

        if (!response.ok) {
          throw new Error(`Failed to fetch assignees (${response.status})`);
        }

        const data = await response.json();
        const rawUsers = useTeamScopedAssignees
          ? data.data
          : useSolutionScopedAssignees
            ? data.people
            : data.data;

        if (!isCancelled) {
          if (data.success && Array.isArray(rawUsers)) {
            if (useTeamScopedAssignees) {
              const knownTeamUsers = rawUsers
                .map((member) => mapTeamMemberAssignee(member, { includeInactive: includedInactiveTeamMembers }))
                .filter((user): user is WorkItemAssigneeOption => Boolean(user));

              setTeamScopedKnownUserIds(
                includedInactiveTeamMembers ? knownTeamUsers.map((user) => user.id) : null
              );
              setFetchedUsers(knownTeamUsers.filter((user) => !user.isInactive));
            } else {
              setTeamScopedKnownUserIds(null);
              setFetchedUsers(
                rawUsers
                  .map(mapAssigneeOption)
                  .filter((user): user is WorkItemAssigneeOption => Boolean(user))
              );
            }
          } else {
            setFetchedUsers([]);
            setTeamScopedKnownUserIds(useTeamScopedAssignees ? [] : null);
          }
        }
      } catch (error) {
        console.error('[WorkItemsViewWrapper] Error fetching users:', error);
        if (!isCancelled) {
          setFetchedUsers([]);
          setTeamScopedKnownUserIds(useTeamScopedAssignees ? [] : null);
        }
      } finally {
        if (!isCancelled) {
          setLoadingUsers(false);
        }
      }
    };

    fetchUsers();

    return () => {
      isCancelled = true;
    };
  }, [resolvedModuleKey, solutionKey, teamId]);

  // When showAllFilter is false, default to selecting the first item type option
  // Use a ref to track the last activeTab to detect navigation changes
  const lastActiveTabRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    // Check if activeTab changed (user navigated to different view)
    const tabChanged = lastActiveTabRef.current !== activeTab;
    lastActiveTabRef.current = activeTab;

    if (!showAllFilter && itemTypeOptions.length > 0) {
      // If tab changed or no selection, set default to first item
      if (tabChanged || selectedItemTypes.length === 0) {
        setSelectedItemTypes([itemTypeOptions[0].id]);
      }
    }
    // Reset selection when showAllFilter becomes true
    if (showAllFilter && selectedItemTypes.length > 0) {
      setSelectedItemTypes([]);
    }
  }, [showAllFilter, itemTypeOptions, activeTab, selectedItemTypes.length]);

  // Set initial viewMode from activeTab when it changes
  useEffect(() => {
    if (!activeTab) {
      setViewMode('list');
      return;
    }

    // Extract the last segment after the last hyphen
    const parts = activeTab.split('-');
    const lastPart = parts[parts.length - 1];

    // Map software development modes to view modes
    const modeMap: Record<string, ViewMode> = {
      'backlog': 'backlog',
      'sprint': 'sprint',
      'kanban': 'board',
      'board': 'board',
      'roadmap': 'roadmap',
      'timeline': 'gantt',
      'analytics': 'analytics',
      'list': 'list',
      'tree': 'tree',
      'gantt': 'gantt',
      'calendar': 'calendar',
      'sla': 'sla',
    };

    setViewMode(modeMap[lastPart] || defaultViewMode || 'list');
  }, [activeTab, defaultViewMode]);

  // Schedule views (time_allocation / resource_schedule) always need BOTH jobs and appointments
  // in the fetched set regardless of the active type pill — the schedule timeline only renders
  // appointments while the unscheduled sidebar shows jobs, and the user expects the same data
  // whether the "Jobs" or "Appointments" pill is active.  The parent (DynamicOperationalView)
  // already merges both types into `workItemType` for schedule views, so we bypass the pill filter.
  const isScheduleView = viewMode === 'board'
    && (defaultBoardViewMode === 'time_allocation' || defaultBoardViewMode === 'resource_schedule');

  // Match API item_type to what the list actually shows (header pills / parent scope), otherwise
  // the server returns a mixed page (e.g. jobs + appointments) and client-side type filters leave a handful of rows.
  // Schedule views are exempt — they always fetch the full `workItemType` (both jobs + appointments).
  const workItemsApiItemType = useMemo(() => {
    if (parentActiveItemTypeFilter && !isScheduleView) {
      return parentActiveItemTypeFilter;
    }
    if (selectedItemTypes.length > 0) {
      return selectedItemTypes.join(',');
    }
    return workItemType;
  }, [parentActiveItemTypeFilter, isScheduleView, selectedItemTypes, workItemType]);

  // Board/calendar schedule views need appointment rows in the fetched set: dragging a job onto the
  // timeline creates an appointment, and itemsForKanban merges appointments from this pool when the
  // active tab filter is job-only. List/tree/gantt keep the narrower API type.
  // For calendar view, also add jobs when appointments are the primary type so the unscheduled-jobs
  // sidebar and drag-to-create-appointment have jobs available. Schedule views (time_allocation /
  // resource_schedule) already receive both types via workItemsApiItemType, so no extra merging here.
  const workItemsDataItemType = useMemo(() => {
    if (viewMode !== 'board' && viewMode !== 'calendar') {
      return workItemsApiItemType;
    }
    const types = (workItemsApiItemType ?? '').split(',').map((t) => t.trim()).filter(Boolean);
    if (types.length === 0) {
      return workItemsApiItemType;
    }
    // Add appointments when jobs/tasks are present (drag-to-schedule, timeline rendering)
    if (!types.includes('appointment') && types.some((t) => t === 'job' || t === 'task')) {
      return [...types, 'appointment'].join(',');
    }
    // For calendar view, also add jobs when appointments are present (unscheduled-jobs sidebar)
    if (viewMode === 'calendar' && !types.includes('job') && types.includes('appointment')) {
      return ['job', ...types].join(',');
    }
    return workItemsApiItemType;
  }, [viewMode, workItemsApiItemType]);

  // Default sort — when provided by the parent (e.g. Processed Expenses
  // defaults to conversion_date desc), otherwise created_at desc.
  const effectiveDefaultSort = useMemo(
    () => defaultSortProp ?? [{ field: 'created_at', direction: 'desc' as const }],
    [defaultSortProp],
  );
  const isDefaultSortActive = useCallback(
    (sort: { field: string; direction: string } | undefined): boolean => {
      if (!sort) return false;
      return effectiveDefaultSort.some(
        (ds) => ds.field === sort.field && ds.direction === sort.direction,
      );
    },
    [effectiveDefaultSort],
  );

  const listKeysetCursor = useMemo(() => {
    // Keyset pagination requires the default sort (created_at desc); skip when
    // a custom sort is active OR when the default sort is not created_at desc
    // (e.g. Processed Expenses defaults to conversion_date desc).
    const defaultIsCreatedAtDesc = effectiveDefaultSort.length === 1
      && effectiveDefaultSort[0].field === 'created_at'
      && effectiveDefaultSort[0].direction === 'desc';
    if (debouncedSort || !defaultIsCreatedAtDesc || viewMode !== 'list' || listGridPage < 2) {
      return { useKeyset: false as const, cursor: undefined as { created_at: string; id: string } | undefined };
    }
    const entry = listPageCursors[listGridPage];
    if (entry?.created_at && entry?.id) {
      return { useKeyset: true as const, cursor: entry };
    }
    return { useKeyset: false as const, cursor: undefined as { created_at: string; id: string } | undefined };
  }, [debouncedSort, effectiveDefaultSort, viewMode, listGridPage, listPageCursors]);

  // Schedule views: build a date-windowed API query so we only fetch items near the
  // visible date instead of loading all records.  The window is ±14 days around the
  // selected date — wide enough to cover day navigation without refetching, narrow
  // enough to stay well within the 500-item limit even for large clients.
  // `schedule_include_unscheduled=true` tells the API to also include items with no
  // scheduled_start (unscheduled jobs for the sidebar) via an OR condition.
  const scheduleDateWindow = useMemo(() => {
    if (!isScheduleView || !scheduleSelectedDate) return null;
    const center = new Date(scheduleSelectedDate);
    const from = new Date(center);
    from.setDate(from.getDate() - 14);
    const to = new Date(center);
    to.setDate(to.getDate() + 14);
    return {
      schedule_date_from: from.toISOString(),
      schedule_date_to: to.toISOString(),
      schedule_include_unscheduled: 'true',
    };
  }, [isScheduleView, scheduleSelectedDate]);

  const mergedWorkItemsAdditionalFilters = useMemo(() => {
    const merged: Record<string, string | number> = { ...(additionalFilters || {}) };
    if (includePlatformSubscriptionInvoices) {
      merged.include_platform_subscription_invoices = 'true';
    }
    if (scheduleDateWindow) {
      Object.assign(merged, scheduleDateWindow);
    }
    return Object.keys(merged).length > 0 ? merged : undefined;
  }, [additionalFilters, includePlatformSubscriptionInvoices, scheduleDateWindow]);

  // Effective server-side status filter: merge the filter bar's status filter
  // (debouncedFilters.status) with the status filter tab (selectedStatusFilter).
  // The status filter tab was previously applied client-side only, which caused
  // a pagination bug: the API returned paginated results without the status
  // filter, so items matching the selected status on later pages were never
  // fetched. By passing selectedStatusFilter to the API, the server filters
  // server-side and pagination works correctly.
  // The invoice case is excluded because invoices use invoiceMatchesListStatusTab
  // for complex multi-status matching that can't be expressed as a single API
  // status parameter.
  const effectiveServerStatusFilter = useMemo(() => {
    const statuses = [...(debouncedFilters.status.length > 0 ? debouncedFilters.status : [])];
    if (selectedStatusFilter && selectedStatusFilter !== 'all' && !(financeMode && financeListDocumentType === 'invoice')) {
      statuses.push(selectedStatusFilter);
    }
    return statuses.length > 0 ? statuses : undefined;
  }, [debouncedFilters.status, selectedStatusFilter, financeMode, financeListDocumentType]);

  // Fetch work items data (list view: paged API + server search; other views: up to 200 rows per fetch)
  // Resolve statuses via SSOT batch endpoint so list views respect workflow state overlays
  const { items, loading, isFetching, refreshItems, pagination } = useWorkItemsWithResolvedStatus({
    itemType: workItemsDataItemType,
    subtype: workItemSubtype, // Pass subtype for filtering (e.g., 'delivery', 'pickup')
    moduleContext, // Only filter by module_context if explicitly provided (don't use templateKey as fallback)
    workspaceId, // Filter by workspace_id (for Jira-like workspaces)
    autoLoad: true, // Always load for all views
    filterByAssignedUser: effectiveFilterByAssignedUser, // Pass through the filter flag (personalMode forces true)
    assignedOnly: effectiveAssignedOnly, // Pass through assigned-only flag (personalMode forces true)
    assignedUserScope,
    deletedFilter: deletedFilterMode,
    statusFilter, // Filter by invoice status (e.g., ['sent', 'outstanding', 'overdue'])
    additionalFilters: mergedWorkItemsAdditionalFilters, // Pass through additional API query params (e.g., source_mailbox_id)
    enableRealtimeSync: true,
    silentRefreshSkipsTotal: true,
    search: debouncedWorkItemsSearch || undefined,
    resolveStatuses: true,
    // Frontend-first + async server-side filter parameters
    status: effectiveServerStatusFilter,
    priority: debouncedFilters.priority.length > 0 ? debouncedFilters.priority : undefined,
    assignedTo: debouncedFilters.assignedTo.length > 0 ? debouncedFilters.assignedTo : undefined,
    tags: debouncedFilters.tags.length > 0 ? debouncedFilters.tags : undefined,
    dateRange: externalDateRange ?? debouncedFilters.dateRange,
    // Schedule views need items sorted by scheduled_start (or created_at for unscheduled items)
    // so upcoming appointments appear within the 200-item API limit. Without this, appointments
    // created long ago but scheduled for today (e.g. APT2391) fall outside the top-200 by created_at
    // and never reach the schedule timeline. COALESCE interleaves scheduled and unscheduled items
    // by relevance, keeping both the timeline and the unscheduled-jobs sidebar populated.
    sortBy: isScheduleView ? 'coalesce_scheduled_created' : debouncedSort?.field,
    sortOrder: isScheduleView ? 'desc' : debouncedSort?.direction,
    page: viewMode === 'list' ? (listKeysetCursor.useKeyset ? 1 : listGridPage) : 1,
    limit: viewMode === 'list' ? listGridPageSize : (isScheduleView ? 500 : 200),
    afterCreatedAt: listKeysetCursor.useKeyset && listKeysetCursor.cursor ? listKeysetCursor.cursor.created_at : null,
    afterId: listKeysetCursor.useKeyset && listKeysetCursor.cursor ? listKeysetCursor.cursor.id : null,
    softReloadOnParamChange: true,
  });

  // Remember keyset cursor for page P+1 from the last row of each fully loaded list page (sequential Next).
  useEffect(() => {
    if (viewMode !== 'list' || loading) {
      return;
    }
    const last = items[items.length - 1];
    setListPageCursors((prev) => {
      const next = { ...prev };
      if (!last?.created_at || !last?.id) {
        return prev;
      }
      const lim = listGridPageSize;
      const total = pagination?.total;
      const fullPage = items.length === lim;
      const lastPageKnown =
        typeof total === 'number'
        && total > 0
        && listGridPage >= Math.max(1, Math.ceil(total / lim));
      if (fullPage && !lastPageKnown) {
        next[listGridPage + 1] = {
          created_at: String(last.created_at),
          id: String(last.id),
        };
      } else {
        delete next[listGridPage + 1];
      }
      return next;
    });
  }, [items, listGridPage, listGridPageSize, loading, pagination?.total, viewMode]);

  useEffect(() => {
    if (viewMode !== 'list') {
      setListPageCursors({});
    }
  }, [viewMode]);

  const appointmentRealtimeIds = useMemo(
    () => items
      .filter((item) => item.item_type === 'appointment')
      .map((item) => String(item.id)),
    [items]
  );

  const {
    statuses: liveAppointmentStatuses,
    seedFromAppointments,
  } = useAppointmentLiveTimers({
    appointmentIds: appointmentRealtimeIds,
    enableTick: false,
  });

  useEffect(() => {
    if (appointmentRealtimeIds.length === 0) {
      return;
    }

    seedFromAppointments(
      items
        .filter((item) => item.item_type === 'appointment')
        .map((item) => ({
          id: String(item.id),
          status: item.status || 'scheduled',
          scheduled_start: item.scheduled_start ?? undefined,
          scheduled_end: item.scheduled_end ?? undefined,
          custom_fields: (item.custom_fields as Record<string, unknown> | undefined) || {},
          assignees: item.assignees?.filter((a): a is WorkItemAssignee & { user_id: number } => a.user_id != null),
          assigned_to: typeof item.assigned_to === 'number' ? item.assigned_to
            : typeof item.assigned_to === 'string' && item.assigned_to.trim() !== ''
              ? Number(item.assigned_to)
              : null,
        }))
    );
  }, [appointmentRealtimeIds.length, items, seedFromAppointments]);

  const effectiveRealtimeItems = useMemo(() => {
    if (liveAppointmentStatuses.size === 0) {
      return items;
    }

    let hasChanges = false;
    const nextItems = items.map((item) => {
      if (item.item_type !== 'appointment') {
        return item;
      }

      const liveStatus = liveAppointmentStatuses.get(String(item.id));
      if (!liveStatus || liveStatus === item.status) {
        return item;
      }

      hasChanges = true;
      return {
        ...item,
        status: liveStatus,
      };
    });

    return hasChanges ? nextItems : items;
  }, [items, liveAppointmentStatuses]);

  useEffect(() => {
    if (!canViewDeletedItems && deletedFilterMode !== 'active') {
      setDeletedFilterMode('active');
    }
  }, [canViewDeletedItems, deletedFilterMode]);

  useEffect(() => {
    setSelectedItems(new Set());
  }, [deletedFilterMode]);

  const handleRestoreItem = async (itemId: string) => {
    try {
      const item = effectiveRealtimeItems.find((entry) => entry.id === itemId);
      const confirmed = await confirm({
        title: 'Restore Work Item',
        message: `Restore ${item?.item_type || 'work item'} "${item?.title || itemId}"? Any deleted child work items linked through the hierarchy will also be restored.`,
        confirmText: 'Restore',
        variant: 'info',
      });

      if (!confirmed) {
        return;
      }

      const token = authService.getTokens()?.access_token;
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`/api/v1/work-items/${itemId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ restore: true }),
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok || !payload?.success) {
        throw new Error(payload?.error || 'Failed to restore work item');
      }

      const restoredChildren = Number(payload?.restored_children || 0);
      notify.success(restoredChildren > 0 ? `Work item restored with ${restoredChildren} child item${restoredChildren === 1 ? '' : 's'}` : 'Work item restored');
      await refreshItems();
    } catch (error: unknown) {
      console.error('[WorkItemsViewWrapper] Failed to restore work item:', error);
      notify.error(getErrorMessage(error, 'Failed to restore work item'));
    }
  };

  // Debug: Log what items are being fetched
  useEffect(() => {
    const appointmentCount = effectiveRealtimeItems.filter(i => i.item_type === 'appointment').length;
    const jobCount = effectiveRealtimeItems.filter(i => i.item_type === 'job').length;
  }, [effectiveRealtimeItems, workItemType]);

  // Listen for workItemCreated events to auto-refresh.
  // Debounce rapid bursts (e.g. Redis pub/sub fan-out) so multiple events
  // batch into a single refetch instead of triggering N concurrent requests.
  useEffect(() => {
    let debounceId: ReturnType<typeof setTimeout> | null = null;
    const handler = () => {
      if (debounceId) clearTimeout(debounceId);
      debounceId = setTimeout(() => {
        debounceId = null;
        refreshItems();
        setDataMutationRefreshToken((prev) => prev + 1);
      }, 300);
    };
    const off = on('workItemCreated', handler);
    const offRefresh = on('workItemRefreshRequested', handler);
    return () => {
      off();
      offRefresh();
      if (debounceId) clearTimeout(debounceId);
    };
  }, [refreshItems]);

  // Listen for sidebar assignee changes and schedule a debounced refresh.
  // This hardens eventual consistency when the first onRefresh() call races the backend transaction.
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    const handleAssigneesChanged = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        refreshItems();
      }, 800);
    };

    window.addEventListener('workItemAssigneesChanged', handleAssigneesChanged);
    return () => {
      window.removeEventListener('workItemAssigneesChanged', handleAssigneesChanged);
      clearTimeout(timeoutId);
    };
  }, [refreshItems]);

  // External refresh trigger — parents can increment `refreshNonce` to request
  // a data refresh without remounting this component (preserves scroll position,
  // filters, and selection). Skips the initial mount (nonce 0 → 0).
  const prevRefreshNonceRef = useRef(refreshNonce);
  useEffect(() => {
    if (refreshNonce === prevRefreshNonceRef.current) return;
    prevRefreshNonceRef.current = refreshNonce;
    if (refreshNonce > 0) {
      refreshItems();
      setDataMutationRefreshToken((prev) => prev + 1);
    }
  }, [refreshNonce, refreshItems]);

  // Keep selectedItem in sync with refreshed items so the detail modal
  // does not display stale status after an external status change.
  useEffect(() => {
    if (!modalOpen || !selectedItem) return;

    const updatedItem = items.find((item) => String(item.id) === String(selectedItem.id));
    if (updatedItem && updatedItem.status !== selectedItem.status) {
      setSelectedItem({ ...selectedItem, ...updatedItem } as WorkItem);
    }
  }, [items, modalOpen, selectedItem]);

  const activeAssigneeSeed = useMemo(() => {
    if (availableAssignees && availableAssignees.length > 0) {
      return availableAssignees;
    }

    return fetchedUsers;
  }, [availableAssignees, fetchedUsers]);

  const inferredInactiveAssignees = useMemo<WorkItemAssigneeOption[]>(() => {
    const activeIds = new Set(activeAssigneeSeed.map((assignee) => assignee.id));
    const knownTeamAssigneeIds = teamScopedKnownUserIds ? new Set(teamScopedKnownUserIds) : null;
    const inferred = new Map<number, WorkItemAssigneeOption>();

    effectiveRealtimeItems.forEach((item) => {
      if (Array.isArray(item.assignees)) {
        item.assignees.forEach((assignee: WorkItemAssignee) => {
          const assigneeId = normalizeAssigneeId(assignee.user_id);
          if (!assigneeId || activeIds.has(assigneeId) || inferred.has(assigneeId)) {
            return;
          }

          if (knownTeamAssigneeIds && !knownTeamAssigneeIds.has(assigneeId)) {
            return;
          }

          const firstName = typeof assignee.first_name === 'string' ? assignee.first_name.trim() : '';
          const lastName = typeof assignee.last_name === 'string' ? assignee.last_name.trim() : '';
          const email = typeof assignee.email === 'string' && assignee.email.trim().length > 0
            ? assignee.email.trim()
            : undefined;
          const name = [firstName, lastName].filter(Boolean).join(' ')
            || email
            || `Former Team Member #${assigneeId}`;

          inferred.set(assigneeId, {
            id: assigneeId,
            name,
            email,
            first_name: firstName || undefined,
            last_name: lastName || undefined,
            isInactive: true,
          });
        });
      }

      const assignedToId = normalizeAssigneeId(item.assigned_to);
      if (!assignedToId || activeIds.has(assignedToId) || inferred.has(assignedToId)) {
        return;
      }

      if (knownTeamAssigneeIds && !knownTeamAssigneeIds.has(assignedToId)) {
        return;
      }

      const fallbackName = getAssignedToName(item) || `Former Team Member #${assignedToId}`;

      inferred.set(assignedToId, {
        id: assignedToId,
        name: fallbackName,
        isInactive: true,
      });
    });

    return Array.from(inferred.values());
  }, [activeAssigneeSeed, effectiveRealtimeItems, teamScopedKnownUserIds]);

  // Merge fetched users with any passed-in assignees and preserve inactive assignees
  // from current work items so terminated/removed users don't disappear with assigned work.
  const mergedAssignees = useMemo(() => {
    const merged = new Map<number, WorkItemAssigneeOption>();

    activeAssigneeSeed.forEach((assignee) => {
      merged.set(assignee.id, assignee);
    });

    inferredInactiveAssignees.forEach((assignee) => {
      if (!merged.has(assignee.id)) {
        merged.set(assignee.id, assignee);
      }
    });

    return Array.from(merged.values());
  }, [activeAssigneeSeed, inferredInactiveAssignees]);

  const kanbanAssignedUsers = useMemo<AssignedUser[]>(() => {
    return mergedAssignees.map(toAssignedUser);
  }, [mergedAssignees]);

  // Items loaded via useWorkItemsData hook

  // NEW: Client-side safety net filter for personal mode (February 2026)
  // Even though the API filters by assigned user, apply a client-side filter
  // to guarantee no unassigned items leak through (e.g., race conditions, caching)
  const personalSafeItems = useMemo(() => {
    if (!isPersonalMode || !currentUserId) return effectiveRealtimeItems;
    return effectiveRealtimeItems.filter(item => {
      // Check legacy assigned_to field
      if (item.assigned_to === currentUserId) return true;
      // Check created_by (only when not assignedOnly mode)
      if (!effectiveAssignedOnly && item.created_by === currentUserId) return true;
      // Check assignees array (work_item_assignees junction table)
      if (item.assignees && Array.isArray(item.assignees)) {
        if (item.assignees.some((assignee) => assignee.user_id === currentUserId)) return true;
      }
      return false;
    });
  }, [effectiveAssignedOnly, effectiveRealtimeItems, isPersonalMode, currentUserId]);

  // NEW: Comprehensive filter/sort hook (December 2025)
  const filterState = useWorkItemsFilter({
    items: personalSafeItems as WorkItem[],
    defaultSort: defaultSortProp ?? [{ field: 'created_at', direction: 'desc' }],
    serverSideSearch: true,
    serverResultTotal: pagination?.total ?? null,
  });
  const activeSearchQuery = filterState.searchQuery.trim();
  const activeAssigneeUserIds = useMemo(() => {
    const assignedTo = filterState.filters.assignedTo;
    if (!assignedTo || assignedTo.length === 0) return undefined;
    const userIds = assignedTo.filter((id): id is number => typeof id === 'number');
    return userIds.length > 0 ? userIds : undefined;
  }, [filterState.filters.assignedTo]);
  const [optimisticClientFilterItems, setOptimisticClientFilterItems] = useState<WorkItem[]>([]);
  const [previousFilterStateRef, setPreviousFilterStateRef] = useState<string>('');

  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebouncedWorkItemsSearch(filterState.searchQuery.trim());
    }, 350);
    return () => window.clearTimeout(id);
  }, [filterState.searchQuery]);

  // Debounce filter changes for async server-side filtering
  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebouncedFilters({
        status: filterState.filters.status || [],
        priority: filterState.filters.priority || [],
        assignedTo: filterState.filters.assignedTo || [],
        tags: filterState.filters.tags || [],
        dateRange: filterState.filters.dateRange,
      });
    }, 350);
    return () => window.clearTimeout(id);
  }, [filterState.filters.status, filterState.filters.priority, filterState.filters.assignedTo, filterState.filters.tags, filterState.filters.dateRange]);

  // Debounce sort changes for async server-side sorting
  useEffect(() => {
    const primarySort = filterState.sorts[0];
    // Only debounce non-default sorts (the API already applies the default sort)
    const isNonDefaultSort = primarySort && !isDefaultSortActive(primarySort);
    const id = window.setTimeout(() => {
      setDebouncedSort(isNonDefaultSort ? { field: primarySort.field, direction: primarySort.direction } : null);
    }, 350);
    return () => window.clearTimeout(id);
  }, [filterState.sorts, isDefaultSortActive]);

  // Check if any filter (including search) is pending backend response
  const activePrimarySort = filterState.sorts[0];
  // Normalize the default sort to an empty key so it matches the debounced
  // sort (which is null for the default, since the API already applies it).
  // Without this, isSortPending would always be true when the default sort is
  // active, permanently marking the backend filter as "pending" and causing
  // stale optimistic client-filtered results to be shown instead of fresh
  // server results.
  const activeSortKey = activePrimarySort
    ? (isDefaultSortActive(activePrimarySort)
      ? ''
      : `${activePrimarySort.field}:${activePrimarySort.direction}`)
    : '';
  const debouncedSortKey = debouncedSort ? `${debouncedSort.field}:${debouncedSort.direction}` : '';
  const isSortPending = activeSortKey !== debouncedSortKey || (isFetching && activeSortKey !== debouncedSortKey);

  const isBackendFilterPending = (
    (activeSearchQuery.length > 0 && (debouncedWorkItemsSearch !== activeSearchQuery || isFetching)) ||
    (filterState.filters.status && filterState.filters.status.length > 0 && !arraysEqual(filterState.filters.status, debouncedFilters.status)) ||
    (filterState.filters.priority && filterState.filters.priority.length > 0 && !arraysEqual(filterState.filters.priority, debouncedFilters.priority)) ||
    (filterState.filters.assignedTo && filterState.filters.assignedTo.length > 0 && !arraysEqual(filterState.filters.assignedTo, debouncedFilters.assignedTo)) ||
    (filterState.filters.tags && filterState.filters.tags.length > 0 && !arraysEqual(filterState.filters.tags, debouncedFilters.tags)) ||
    (filterState.filters.dateRange !== debouncedFilters.dateRange) ||
    isSortPending
  );

  // Helper to compare arrays for equality
  function arraysEqual<T>(a: T[], b: T[]): boolean {
    if (a.length !== b.length) return false;
    return a.every((val, index) => val === b[index]);
  }

  useEffect(() => {
    // Create a signature of the current filter state
    const currentFilterSignature = JSON.stringify({
      search: filterState.searchQuery,
      status: filterState.filters.status,
      priority: filterState.filters.priority,
      assignedTo: filterState.filters.assignedTo,
      tags: filterState.filters.tags,
      dateRange: filterState.filters.dateRange,
      sort: activeSortKey,
    });

    // If no filters are active, clear optimistic items
    const hasNoActiveFilters =
      !filterState.searchQuery &&
      !filterState.filters.status?.length &&
      !filterState.filters.priority?.length &&
      !filterState.filters.assignedTo?.length &&
      !filterState.filters.tags?.length &&
      !filterState.filters.dateRange &&
      (!activePrimarySort || (activePrimarySort.field === 'created_at' && activePrimarySort.direction === 'desc'));

    if (hasNoActiveFilters) {
      setPreviousFilterStateRef('');
      setOptimisticClientFilterItems([]);
      return;
    }

    // If filter state changed, capture current client-filtered results
    if (previousFilterStateRef !== currentFilterSignature) {
      setPreviousFilterStateRef(currentFilterSignature);
      // Compute client-side filtered items with skipSearch: false so that
      // search filtering is applied for instant feedback, even when
      // serverSideSearch is true (where filterState.filteredItems skips
      // client-side search to preserve server results).
      const clientFiltered = applyWorkItemFilterRules(
        personalSafeItems as WorkItem[],
        filterState.searchQuery,
        filterState.filters,
        { skipSearch: false },
      );
      setOptimisticClientFilterItems(clientFiltered);
      return;
    }

    // If backend is done and filters match, clear optimistic items
    if (!isBackendFilterPending && previousFilterStateRef === currentFilterSignature) {
      setOptimisticClientFilterItems([]);
    }
  }, [
    filterState.searchQuery,
    filterState.filters,
    filterState.filters.status,
    filterState.filters.priority,
    filterState.filters.assignedTo,
    filterState.filters.tags,
    filterState.filters.dateRange,
    filterState.filteredItems,
    isBackendFilterPending,
    previousFilterStateRef,
    activePrimarySort,
    activeSortKey,
    personalSafeItems,
  ]);

  useEffect(() => {
    setListGridPage(1);
    setListPageCursors({});
  }, [debouncedWorkItemsSearch, debouncedFilters.status, debouncedFilters.priority, debouncedFilters.assignedTo, debouncedFilters.tags, debouncedFilters.dateRange, debouncedSort, selectedStatusFilter]);

  useEffect(() => {
    setListGridPage(1);
    setListPageCursors({});
  }, [deletedFilterMode]);

  useEffect(() => {
    setListGridPage(1);
    setListPageCursors({});
  }, [workItemsDataItemType]);

  useEffect(() => {
    if (viewMode !== 'list' || !pagination) {
      return;
    }
    const maxPage = pagination.pages
      || Math.max(1, Math.ceil(Math.max(pagination.total, 0) / Math.max(listGridPageSize, 1)));
    if (listGridPage > maxPage) {
      setListGridPage(maxPage);
    }
  }, [viewMode, pagination, listGridPage, listGridPageSize]);
  const headerCustomFilters = filterState.filters.customFilters;
  const updateWorkItemsFilter = filterState.updateFilter;

  useEffect(() => {
    const currentHeaderStatFilter = headerCustomFilters?.statCardFilter as WorkItemStatFilter | undefined;
    const desiredHeaderStatFilter = activeHeaderStatFilter ?? undefined;

    if (currentHeaderStatFilter === desiredHeaderStatFilter) {
      return;
    }

    const nextCustomFilters = { ...(headerCustomFilters || {}) };

    if (desiredHeaderStatFilter) {
      nextCustomFilters.statCardFilter = desiredHeaderStatFilter;
    } else {
      delete nextCustomFilters.statCardFilter;
    }

    updateWorkItemsFilter(
      'customFilters',
      Object.keys(nextCustomFilters).length > 0 ? nextCustomFilters : undefined
    );
  }, [activeHeaderStatFilter, headerCustomFilters, updateWorkItemsFilter]);

  useEffect(() => {
    const currentRequestedDocType = headerCustomFilters?.requestedDocType as string | undefined;
    const desiredRequestedDocType = activeDocTypeFilter ?? undefined;

    if (currentRequestedDocType === desiredRequestedDocType) {
      return;
    }

    const nextCustomFilters = { ...(headerCustomFilters || {}) };

    if (desiredRequestedDocType) {
      nextCustomFilters.requestedDocType = desiredRequestedDocType;
    } else {
      delete nextCustomFilters.requestedDocType;
    }

    updateWorkItemsFilter(
      'customFilters',
      Object.keys(nextCustomFilters).length > 0 ? nextCustomFilters : undefined
    );
  }, [activeDocTypeFilter, headerCustomFilters, updateWorkItemsFilter]);

  const shouldFetchServerStatusCounts =
    (showFilterBarStatusFilter || statusFilterOptions.length > 0)
    && !(financeMode && financeListDocumentType === 'invoice' && statusFilterOptions.length > 0);

  const statusCountItemTypeScope = useMemo(
    () => mergeScopedFilterValues(
      normalizeQueryFilterValues(workItemsApiItemType),
      normalizeQueryFilterValues(filterState.filters.itemType),
    ),
    [filterState.filters.itemType, workItemsApiItemType]
  );

  const statusCountItemSubtypeScope = useMemo(
    () => mergeScopedFilterValues(
      normalizeQueryFilterValues(workItemSubtype),
      normalizeQueryFilterValues(filterState.filters.itemSubtype),
    ),
    [filterState.filters.itemSubtype, workItemSubtype]
  );

  const reservedStatusCountQueryParams = useMemo(() => new Set([
    'include_status_counts',
    'counts_only',
    'include_total',
  ]), []);

  useEffect(() => {
    if (!shouldFetchServerStatusCounts) {
      setServerStatusCounts(null);
      return;
    }

    if (statusCountItemTypeScope.hasEmptyIntersection || statusCountItemSubtypeScope.hasEmptyIntersection) {
      setServerStatusCounts({});
      return;
    }

    if (debouncedFilters.assignedTo.includes(null)) {
      setServerStatusCounts(null);
      return;
    }

    const token = authService.getTokens()?.access_token;
    if (!token) {
      setServerStatusCounts(null);
      return;
    }

    /** When deps flicker before this debounced run completes, avoid abort()-driven cancellations in DevTools. */
    let effectDiscarded = false;

    const debounceTimer = window.setTimeout(() => {
      void (async () => {
        try {
          const params = new URLSearchParams();

          statusCountItemTypeScope.values.forEach((itemTypeValue) => {
            params.append('item_type', itemTypeValue);
          });

          statusCountItemSubtypeScope.values.forEach((itemSubtypeValue) => {
            params.append('item_subtype', itemSubtypeValue);
          });

          if (moduleContext) {
            params.append('module_context', moduleContext);
          }

          if (workspaceId) {
            params.append('workspace_id', workspaceId);
          }

          if (effectiveFilterByAssignedUser) {
            params.append('filter_by_assigned_user', 'true');
          }

          if (effectiveAssignedOnly) {
            params.append('assigned_only', 'true');
          }

          assignedUserScope?.forEach((userId) => {
            if (Number.isFinite(userId)) {
              params.append('assigned_user_scope', String(userId));
            }
          });

          if (deletedFilterMode !== 'active') {
            params.append('deleted_filter', deletedFilterMode);
          }

          statusFilter?.forEach((statusValue) => {
            params.append('status', statusValue);
          });

          if (mergedWorkItemsAdditionalFilters) {
            Object.entries(mergedWorkItemsAdditionalFilters).forEach(([key, value]) => {
              if (reservedStatusCountQueryParams.has(key)) {
                return;
              }

              if (value === undefined || value === null) {
                return;
              }

              if (Array.isArray(value)) {
                value.forEach((entry) => {
                  if (entry !== undefined && entry !== null) {
                    params.append(key, String(entry));
                  }
                });
                return;
              }

              params.append(key, String(value));
            });
          }

          debouncedFilters.priority.forEach((priorityValue) => {
            params.append('priority', priorityValue);
          });

          debouncedFilters.assignedTo.forEach((assignedUserId) => {
            if (assignedUserId !== null) {
              params.append('assigned_to', String(assignedUserId));
            }
          });

          debouncedFilters.tags.forEach((tag) => {
            params.append('tags', tag);
          });

          if (selectedContact?.value != null) {
            params.append('contact_id', String(selectedContact.value));
          }

          const trimmedSearchQuery = debouncedWorkItemsSearch.trim();
          if (trimmedSearchQuery) {
            params.append('search', trimmedSearchQuery);
          }

          appendWorkItemsDateRangeParams(params, externalDateRange ?? debouncedFilters.dateRange);

          params.append('include_status_counts', 'true');
          params.append('counts_only', 'true');
          params.append('include_total', 'false');

          const response = await fetch(`/api/v1/work-items?${params.toString()}`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          if (effectDiscarded) {
            return;
          }

          if (!response.ok) {
            throw new Error(`Failed to load status counts (${response.status})`);
          }

          const payload = await response.json();
          if (effectDiscarded) {
            return;
          }

          const nextStatusCounts = payload?.status_counts && typeof payload.status_counts === 'object'
            ? payload.status_counts as Record<string, number>
            : {};

          if (!effectDiscarded) {
            setServerStatusCounts(nextStatusCounts);
          }
        } catch (error) {
          if (effectDiscarded) {
            return;
          }

          console.error('[WorkItemsViewWrapper] Failed to load server status counts:', error);
          setServerStatusCounts(null);
        }
      })();
    }, SERVER_STATUS_COUNTS_DEBOUNCE_MS);

    return () => {
      effectDiscarded = true;
      window.clearTimeout(debounceTimer);
    };
  }, [
    dataMutationRefreshToken,
    assignedUserScope,
    mergedWorkItemsAdditionalFilters,
    debouncedFilters.assignedTo,
    debouncedFilters.dateRange,
    debouncedFilters.priority,
    debouncedFilters.tags,
    debouncedWorkItemsSearch,
    deletedFilterMode,
    effectiveAssignedOnly,
    effectiveFilterByAssignedUser,
    moduleContext,
    reservedStatusCountQueryParams,
    selectedContact,
    shouldFetchServerStatusCounts,
    statusCountItemSubtypeScope,
    statusCountItemTypeScope,
    statusFilter,
    workspaceId,
    externalDateRange,
  ]);

  // Extract unique contacts from items for the contact filter dropdown
  const availableContacts = React.useMemo(() => {
    const contactMap = new Map<number, ContactOption>();

    effectiveRealtimeItems.forEach(item => {
      if (item.contact_id && item.contact_name) {
        // Only add if we haven't seen this contact_id before
        if (!contactMap.has(item.contact_id)) {
          contactMap.set(item.contact_id, {
            value: item.contact_id,
            label: item.contact_name,
            contact_type: getContactType(item),
            email: item.contact_email || undefined,
            phone: item.contact_phone || undefined
          });
        }
      }
    });

    // Convert to array and sort alphabetically by label
    return Array.from(contactMap.values()).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [effectiveRealtimeItems]);

  const applyViewLevelFilters = useCallback((itemsToFilter: WorkItem[]) => {
    let result = [...itemsToFilter];

    // Schedule views (time_allocation / resource_schedule) must show both jobs and appointments
    // regardless of the active type pill — the timeline renders appointments while the unscheduled
    // sidebar shows jobs.  Filtering by the pill type here would strip one of the two sets.
    if (parentActiveItemTypeFilter && !isScheduleView) {
      // parentActiveItemTypeFilter may be a comma-separated type list (e.g.
      // 'ticket,support_ticket' facet pills) — the API layer splits it, so the
      // client filter must too or every row is dropped.
      const allowedTypes = new Set(
        parentActiveItemTypeFilter.split(',').map((t) => t.trim()).filter(Boolean),
      );
      result = result.filter((item) => allowedTypes.has(item.item_type || ''));
    }

    if (selectedItemTypes.length > 0) {
      result = result.filter((item) => selectedItemTypes.includes(item.item_type || ''));
    }

    if (agentOnly) {
      result = result.filter((item) => {
        const cf = item.custom_fields as Record<string, unknown> | undefined;
        return !!cf && typeof cf === 'object' && !!cf.agent && typeof cf.agent === 'object';
      });
    }

    if (selectedStatusFilter && selectedStatusFilter !== 'all') {
      result = result.filter((item) => {
        if (financeMode && financeListDocumentType === 'invoice') {
          return invoiceMatchesListStatusTab(item as never, selectedStatusFilter);
        }
        return item.status === selectedStatusFilter;
      });
    }

    if (excludeStatuses && excludeStatuses.length > 0) {
      result = result.filter((item) => !excludeStatuses.includes(item.status || ''));
    }

    if (selectedContact) {
      result = result.filter((item) => item.contact_id === selectedContact.value);
    }

    return result;
  }, [
    agentOnly,
    excludeStatuses,
    financeListDocumentType,
    financeMode,
    isScheduleView,
    parentActiveItemTypeFilter,
    selectedContact,
    selectedItemTypes,
    selectedStatusFilter,
  ]);

  const blendedSearchItems = useMemo(() => {
    const serverFilteredItems = filterState.filteredItems as WorkItem[];

    // If no backend filter is pending or no optimistic items, return server results
    if (
      !isBackendFilterPending ||
      optimisticClientFilterItems.length === 0 ||
      previousFilterStateRef === ''
    ) {
      return serverFilteredItems;
    }

    // During pending state, show only optimistic client-filtered items.
    // These include search filtering (skipSearch: false) for instant feedback.
    // serverFilteredItems now skips client-side search (serverSideSearch: true),
    // so merging them would show all items from the current page regardless of
    // the search query.  Once the backend responds, isBackendFilterPending
    // becomes false and we return serverFilteredItems (the server's search
    // results) directly.
    return optimisticClientFilterItems;
  }, [filterState.filteredItems, isBackendFilterPending, optimisticClientFilterItems, previousFilterStateRef]);

  // Filter items by selected item types (legacy) + new filter system + status filter + contact filter
  const filteredItems = React.useMemo(() => {
    return applyViewLevelFilters(blendedSearchItems);
  }, [applyViewLevelFilters, blendedSearchItems]);

  // Final row count vs server total (toolbar + pills match the grid; avoids "25 of 211" while only 3 rows render)
  const filterBarStatsSummary = React.useMemo(
    () => ({
      filtered: filteredItems.length,
      total: pagination?.total ?? filteredItems.length,
    }),
    [filteredItems.length, pagination?.total],
  );

  // For Kanban board: Include appointments AND jobs even if selectedItemTypes doesn't include them
  // This ensures Schedule (time_allocation) view can see appointments on the timeline
  // and always has jobs available for the unscheduled sidebar (drag-to-schedule).
  // Supplemental rows must still respect toolbar filters (date range, search, status, etc.) —
  // merging from raw `items` previously bypassed dateRange and made ResourceScheduleTimeline ignore it.
  const itemsForKanban = React.useMemo(() => {
    let result = [...filteredItems];
    const idSet = new Set(result.map((i) => String(i.id)));

    const mergePoolIgnoringItemType = applyWorkItemFilterRules(
      personalSafeItems as WorkItem[],
      filterState.searchQuery,
      filterState.filters,
      { ignoreItemTypeFilters: true, skipSearch: true },
    );

    const hasAppointments = result.some((i) => i.item_type === 'appointment');
    if (!hasAppointments) {
      for (const row of mergePoolIgnoringItemType) {
        if (row.item_type !== 'appointment') continue;
        const id = String(row.id);
        if (idSet.has(id)) continue;
        result.push(row);
        idSet.add(id);
      }
    }

    const hasJobs = result.some((i) => i.item_type === 'job');
    if (!hasJobs) {
      for (const row of mergePoolIgnoringItemType) {
        if (row.item_type !== 'job') continue;
        const id = String(row.id);
        if (idSet.has(id)) continue;
        result.push(row);
        idSet.add(id);
      }
    }

    return result;
  }, [filteredItems, personalSafeItems, filterState.searchQuery, filterState.filters]);

  // For list view, show all items including appointments
  const listViewItems = React.useMemo(() => {
    return filteredItems;
  }, [filteredItems]);

  const dataGridItems = useMemo<DataGridWorkItem[]>(() => {
    return listViewItems.map((item) => ({
      ...item,
      item_number: item.item_number || String(item.id),
      item_type: item.item_type || 'work_item',
      title: item.title || '',
      description: item.description ?? undefined,
      status: item.status || 'open',
      priority: (item.priority || 'medium') as DataGridWorkItem['priority'],
      progress_percentage: typeof item.progress_percentage === 'number'
        ? item.progress_percentage
        : Number(item.progress_percentage || 0),
      tags: Array.isArray(item.tags) ? item.tags : [],
      created_by: typeof item.created_by === 'number' ? item.created_by : 0,
      created_at: item.created_at || new Date().toISOString(),
      updated_at: item.updated_at || item.created_at || new Date().toISOString(),
    }) as DataGridWorkItem);
  }, [listViewItems]);

  const dataGridItemTypeOptions = useMemo<DataGridItemTypeOption[]>(() => {
    return itemTypeOptions.map((option) => ({
      ...option,
      icon: option.icon as DataGridItemTypeOption['icon'],
    }));
  }, [itemTypeOptions]);

  // filteredItems calculated from filterState + selectedItemTypes

  // Hierarchy utilities
  const { buildTree } = useWorkItemHierarchy();

  // Build tree structure from flat items (use filtered items)
  const treeData = React.useMemo(() => {
    if (viewMode === 'tree' && filteredItems.length > 0) {
      return buildTree(filteredItems as unknown as HierarchyWorkItem[]) as unknown as TreeWorkItem[];
    }
    return [];
  }, [filteredItems, viewMode, buildTree]);

  const resolvedTreeVisibleColumns = useMemo(
    () => sanitizeTreeVisibleColumns(persistedTreeVisibleColumns),
    [persistedTreeVisibleColumns]
  );

  const treeColumnCounts = useMemo<Record<TreeVisibleColumnKey, number>>(() => ({
    type: filteredItems.length,
    assignees: filteredItems.filter((item) => Array.isArray(item.assignees) && item.assignees.length > 0).length,
    progress: filteredItems.filter((item) => {
      const progressValue = typeof item.progress_percentage === 'number' ? item.progress_percentage : Number(item.progress_percentage || 0);
      return progressValue > 0 || item.estimated_hours !== undefined && item.estimated_hours !== null;
    }).length,
    status: filteredItems.length,
    actions: filteredItems.length,
  }), [filteredItems]);

  const gridColumnValueCounts = useMemo<Record<string, number>>(() => {
    const counts: Record<string, number> = {};
    gridToggleableColumns.forEach((column) => {
      counts[column.key] = dataGridItems.filter((item) => hasNonEmptyGridColumnValue(item, column.key)).length;
    });
    return counts;
  }, [dataGridItems, gridToggleableColumns]);

  const listToolbarModel = useMemo<BoardToolbarModel | null>(() => {
    if (!hideBoardToolbar || viewMode !== 'list') {
      return null;
    }

    const visibleColumns = sanitizeWorkItemsDataGridVisibleColumns(persistedListVisibleColumns, gridColumnDefinitions);
    const fallbackColumnKey = gridToggleableColumns.find((column) => column.key === 'title')?.key || gridToggleableColumns[0]?.key;

    return {
      ...buildBaseToolbarModel(),
      showColumnSettings: false,
      visibleColumnCount: visibleColumns.length,
      totalColumnCount: gridToggleableColumns.length,
      onToggleAllColumns: () => {
        const allColumnKeys = gridToggleableColumns.map((column) => column.key);
        const nextColumnKeys = visibleColumns.length < allColumnKeys.length
          ? allColumnKeys
          : (fallbackColumnKey ? [fallbackColumnKey] : allColumnKeys);
        handleListVisibleColumnsChange(nextColumnKeys);
      },
      columnOptions: gridToggleableColumns.map((column) => ({
        key: column.key,
        label: column.mobileLabel || column.label,
        count: gridColumnValueCounts[column.key] ?? dataGridItems.length,
        visible: visibleColumns.includes(column.key),
        disabled: visibleColumns.length === 1 && visibleColumns.includes(column.key),
        colorClassName: 'bg-blue-500',
        onToggle: () => {
          const nextColumnKeys = visibleColumns.includes(column.key)
            ? visibleColumns.filter((key) => key !== column.key)
            : [...visibleColumns, column.key];
          handleListVisibleColumnsChange(nextColumnKeys);
        },
      })),
    };
  }, [dataGridItems.length, gridColumnDefinitions, gridColumnValueCounts, gridToggleableColumns, handleListVisibleColumnsChange, hideBoardToolbar, persistedListVisibleColumns, viewMode]);

  const treeToolbarModel = useMemo<BoardToolbarModel | null>(() => {
    if (viewMode !== 'tree') {
      return null;
    }

    return {
      ...buildBaseToolbarModel(),
      showColumnSettings: false,
      visibleColumnCount: resolvedTreeVisibleColumns.length,
      totalColumnCount: DEFAULT_TREE_VISIBLE_COLUMNS.length,
      onToggleAllColumns: () => {
        const nextColumns = resolvedTreeVisibleColumns.length < DEFAULT_TREE_VISIBLE_COLUMNS.length
          ? DEFAULT_TREE_VISIBLE_COLUMNS
          : [];
        handleTreeVisibleColumnsChange(nextColumns);
      },
      columnOptions: DEFAULT_TREE_VISIBLE_COLUMNS.map((columnKey) => ({
        key: columnKey,
        label: TREE_VISIBLE_COLUMN_LABELS[columnKey],
        count: treeColumnCounts[columnKey],
        visible: resolvedTreeVisibleColumns.includes(columnKey),
        disabled: false,
        colorClassName: 'bg-indigo-500',
        onToggle: () => {
          const nextColumns = resolvedTreeVisibleColumns.includes(columnKey)
            ? resolvedTreeVisibleColumns.filter((key) => key !== columnKey)
            : [...resolvedTreeVisibleColumns, columnKey];
          handleTreeVisibleColumnsChange(nextColumns);
        },
      })),
    };
  }, [handleTreeVisibleColumnsChange, resolvedTreeVisibleColumns, treeColumnCounts, viewMode]);

  const activeManagedToolbarModel = useMemo<BoardToolbarModel | null>(() => {
    if (!hideBoardToolbar) {
      return null;
    }

    if (viewMode === 'list') {
      return listToolbarModel;
    }

    if (viewMode === 'tree') {
      return treeToolbarModel;
    }

    if (viewMode === 'board') {
      return kanbanToolbarModel;
    }

    return null;
  }, [hideBoardToolbar, kanbanToolbarModel, listToolbarModel, treeToolbarModel, viewMode]);

  const activeManagedToolbarSignature = useMemo(
    () => getBoardToolbarModelSignature(activeManagedToolbarModel),
    [activeManagedToolbarModel]
  );

  const lastPublishedToolbarSignatureRef = useRef('null');

  useEffect(() => {
    if (!onBoardToolbarChange) {
      lastPublishedToolbarSignatureRef.current = 'null';
      return;
    }

    if (activeManagedToolbarSignature === lastPublishedToolbarSignatureRef.current) {
      return;
    }

    lastPublishedToolbarSignatureRef.current = activeManagedToolbarSignature;
    onBoardToolbarChange(activeManagedToolbarModel);
  }, [activeManagedToolbarModel, activeManagedToolbarSignature, onBoardToolbarChange]);

  // Handle view item (opens detail modal)
  const handleViewItem = async (item: WorkItem | DataWorkItem | string) => {
    let targetItem: WorkItem | DataWorkItem | undefined;

    if (typeof item === 'string') {
      // Find item by ID
      targetItem = items.find(i => i.id === item);
    } else {
      targetItem = item;
    }

    if (!targetItem) return;

    // Use custom handler if provided (e.g., for SupportDesk to use SupportTicketDetailModal)
    if (onItemClick) {
      onItemClick(targetItem as WorkItem);
      return;
    }

    // Billing submissions are first-class finance documents and should open in DocumentModal.
    // Fetch the full document first — dispatching a sparse { id } opens the
    // modal with an empty header (no number/date/contact) and zeroed totals,
    // even though the BOM lines still load.
    if (targetItem.item_type === 'billing_submission' && typeof window !== 'undefined') {
      let documentPayload: Record<string, unknown> = { id: String(targetItem.id) };
      const token = authService.getTokens()?.access_token;
      if (token) {
        try {
          const res = await fetch(`/api/v1/work-items/documents/${encodeURIComponent(String(targetItem.id))}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const json = await res.json().catch(() => null);
          if (res.ok && json?.success && json.data) {
            documentPayload = json.data;
          }
        } catch {
          // Fall through to the sparse payload — DocumentModal still loads BOM lines.
        }
      }
      window.dispatchEvent(
        new CustomEvent('openDocumentModal', {
          detail: { doc_type: 'billing_submission', mode: 'edit', document: documentPayload },
        })
      );
      return;
    }

    if (workItemOpener) {
      setModalOpen(false);
      setSelectedItem(null);
      workItemOpener.openWorkItemDirect(targetItem as WorkItem, {
        initialTab: detailInitialTab,
        onRefresh: refreshItems,
        listModuleContext: moduleContext ?? null,
        activeSolutionKey: solutionKey ?? null,
      });
      return;
    }

    // Open the item directly — each item type now has its own visual identity
    // in WorkItemDetailModal (appointments = green, jobs = teal, etc.)
    setSelectedItem(targetItem as WorkItem);
    setModalOpen(true);
  };

  // Handle item click in tree view
  const handleTreeItemClick = (item: TreeWorkItem) => {
    handleViewItem(item);
  };

  // Handle add child in tree view
  const handleAddChild = (parent: TreeWorkItem) => {
    // TODO: Open create modal with parent pre-selected
    if (workItemOpener) {
      setModalOpen(false);
      setSelectedItem(null);
      workItemOpener.openWorkItemDirect(parent as unknown as WorkItem, {
        initialTab: detailInitialTab,
        onRefresh: refreshItems,
        listModuleContext: moduleContext ?? null,
      });
      return;
    }

    setSelectedItem(parent);
    setModalOpen(true);
  };

  // NEW: Bulk Selection Handlers (February 2026)
  const handleToggleSelection = (itemId: string) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    const allIds = listViewItems.map(item => item.id);
    setSelectedItems(new Set(allIds));
  };

  const handleClearSelection = () => {
    setSelectedItems(new Set());
  };

  // Propagate selection changes to parent
  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(selectedItems);
    }
  }, [selectedItems, onSelectionChange]);

  // Propagate loaded items to parent so they can avoid redundant refetches
  useEffect(() => {
    if (onItemsLoaded) {
      onItemsLoaded(filteredItems);
    }
  }, [filteredItems, onItemsLoaded]);

  // Payment Reminder state & handler (April 2026)
  const [sendingReminders, setSendingReminders] = useState(false);

  const handleSendPaymentReminder = async () => {
    type ReminderDoc = PaymentReminderCandidate & {
      contact_email: string;
      contact_id: number;
    };

    const selected = listViewItems.filter((item) => selectedItems.has(item.id)) as PaymentReminderCandidate[];
    const unsettled = selected.filter((item) =>
      item.payment_status !== 'paid'
      && Number(item.balance_due ?? item.grand_total ?? 0) > 0
    );
    if (unsettled.length === 0) {
      notify.error('No unsettled invoices selected');
      return;
    }
    const withEmail = unsettled.filter((item): item is ReminderDoc => {
      const hasEmail = typeof item.contact_email === 'string' && item.contact_email.trim().length > 0;
      const hasContactId = typeof item.contact_id === 'number' && Number.isFinite(item.contact_id);

      return hasEmail && hasContactId;
    });
    if (withEmail.length === 0) {
      notify.error('None of the selected invoices have a contact with an email');
      return;
    }

    const confirmed = await confirm({
      title: 'Send Payment Reminders',
      message: `Send payment reminder emails for ${withEmail.length} unsettled invoice${withEmail.length === 1 ? '' : 's'}? Each contact will receive a customer statement with the outstanding balance.`,
      confirmText: 'Send Now',
      cancelText: 'Cancel',
      onConfirm: async () => { },
    });
    if (!confirmed) return;

    setSendingReminders(true);
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) throw new Error('Not authenticated');
      const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

      let sent = 0;
      let failed = 0;

      // Group invoices by contact to send one statement per contact
      const byContact = new Map<number, ReminderDoc[]>();
      for (const doc of withEmail) {
        const cid = doc.contact_id;
        if (!byContact.has(cid)) byContact.set(cid, []);
        byContact.get(cid)?.push(doc);
      }

      for (const [contactId, docs] of byContact) {
        try {
          // Step 1: Generate customer statement for this contact
          const reportRes = await fetch('/api/v1/finance-reports', {
            method: 'POST',
            headers,
            body: JSON.stringify({
              action: 'generateCustomerStatement',
              contact_id: contactId,
              date_from: '2020-01-01',
              date_to: new Date().toISOString().split('T')[0],
            }),
          });
          if (!reportRes.ok) { failed += docs.length; continue; }
          const reportJson = await reportRes.json();
          const reportData = reportJson.data;
          if (!reportData) { failed += docs.length; continue; }

          const contactEmail = docs[0].contact_email;
          const contactName = docs[0].contact_name || 'Customer';
          const invoiceNums = docs.map((doc) => doc.item_number || doc.doc_number || doc.id).join(', ');

          // Step 2: Email the statement immediately
          const emailRes = await fetch('/api/v1/reports/email', {
            method: 'POST',
            headers,
            body: JSON.stringify({
              documentType: 'customer_statement',
              params: { reportData },
              email: {
                to: [contactEmail],
                subject: `Payment Reminder — Outstanding Statement for ${contactName}`,
                message: `This is a payment reminder regarding outstanding invoice${docs.length === 1 ? '' : 's'} ${invoiceNums}. Please find your latest customer statement attached.`,
                emailTemplateCode: 'customer_statement_delivery',
                attachPdf: true,
              },
            }),
          });
          if (emailRes.ok) { sent += docs.length; } else { failed += docs.length; }
        } catch { failed += docs.length; }
      }
      if (sent > 0) notify.success(`Payment reminder${sent === 1 ? '' : 's'} sent for ${sent} invoice${sent === 1 ? '' : 's'}`);
      if (failed > 0) notify.error(`${failed} reminder${failed === 1 ? '' : 's'} failed to send`);
      handleClearSelection();
    } catch (error: unknown) {
      notify.error(getErrorMessage(error, 'Failed to send reminders'));
    } finally {
      setSendingReminders(false);
    }
  };

  // Update status (for Kanban)
  const handleUpdateStatus = async (itemId: string, newStatus: string) => {
    if (readOnly) {
      notify.error('Status changes are managed from the item detail — drag editing is disabled here.');
      return;
    }
    try {
      // Admin-only restriction: only admins can move items to 'closed'
      // Staff can only move items to 'resolved' — admin must approve the close
      if (workflowField === 'status' && newStatus === 'closed' && !permissions.can_admin) {
        throw new Error('Only administrators can close items. Staff can resolve items — an admin must review and close.');
      }

      const token = authService.getTokens()?.access_token;
      if (!token) {
        throw new Error('No authentication token');
      }

      // Determine which field to update based on workflowField prop
      const fieldToUpdate = workflowField || 'status';

      // Check process gates for blockers on any status transition (all item types).
      // The server enforces this too (423 gate_blocked) — this client preflight exists
      // so Kanban drag-and-drop can revert the optimistic move and open the detail
      // modal where ProcessGatePanel lets the user complete the required steps.
      const item = items.find((i) => i.id === itemId);
      const normalizedNewStatus = normalizeWorkflowStatusKey(newStatus);
      const normalizedCurrentStatus = normalizeWorkflowStatusKey(item?.status);

      if (normalizedNewStatus && normalizedNewStatus !== normalizedCurrentStatus) {
        const gatesResponse = await fetch(`/api/v1/work-items/${itemId}/process-gates?target_status=${encodeURIComponent(newStatus)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const gatesData = gatesResponse.ok ? await gatesResponse.json().catch(() => null) : null;
        const isBlocked = gatesData?.success && gatesData.data?.is_blocked;

        if (isBlocked) {
          const blockingGateNames = (Array.isArray(gatesData.data?.process_gates) ? gatesData.data.process_gates : [])
            .filter((gate: { is_enforced?: boolean; gate_released?: boolean }) => gate.is_enforced && !gate.gate_released)
            .map((gate: { template_name?: string }) => gate.template_name)
            .filter((name: unknown): name is string => typeof name === 'string' && name.trim().length > 0);
          const gateLabel = gatesData.data?.has_process_gate_templates ? 'process checklist' : 'required form';
          const gateNamesText = blockingGateNames.length > 0 ? `: ${blockingGateNames.join(', ')}` : '';

          // For Kanban drag-and-drop, open the detail modal so user can use DetailsSidebar
          // to complete the required steps or provide an override reason
          await handleViewItem(itemId);
          throw new Error(`Status change blocked — the ${gateLabel} must be completed first${gateNamesText}. The required steps are open in the details panel.`);
        }
      }

      const response = await fetch(`/api/v1/work-items/${itemId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ [fieldToUpdate]: newStatus })
      });

      if (!response.ok) {
        const text = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(text);
        } catch {
          errorData = { error: text || 'Failed to update status' };
        }
        throw new Error(errorData.error || 'Failed to update status');
      }

      const text = await response.text();

      if (!text) {
        await refreshItems();
        return;
      }

      // Refresh items
      await refreshItems();
    } catch (error) {
      console.error('[WorkItemsViewWrapper] Failed to update status:', error);
      throw error;
    }
  };

  // Update assignment (for Kanban assignment view)
  const handleUpdateAssignment = async (itemId: string, assignedTo: number | null) => {
    if (readOnly) {
      notify.error('Assignment changes are disabled on this surface.');
      return;
    }
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) {
        throw new Error('No authentication token');
      }

      // Get current item to check status
      const currentItem = effectiveRealtimeItems.find(item => item.id === itemId);
      const currentStatus = currentItem?.status;

      // If assigning someone and status is 'open' or 'new', move to 'assigned'
      const shouldUpdateStatus = assignedTo && (currentStatus === 'open' || currentStatus === 'new');

      const response = await fetch(`/api/v1/work-items/${itemId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          assigned_to: assignedTo,
          ...(shouldUpdateStatus && { status: 'assigned' })
        })
      });

      if (!response.ok) {
        const text = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(text);
        } catch {
          errorData = { error: text || 'Failed to update assignment' };
        }
        throw new Error(errorData.error || 'Failed to update assignment');
      }

      const text = await response.text();
      if (!text) {
        await refreshItems();
        return;
      }

      // Also update the assignees array so it shows in list view
      // Step 1: Remove all existing assignees
      try {
        const getResponse = await fetch(`/api/v1/work-items/${itemId}/assignees`, {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (getResponse.ok) {
          const { data: existingAssignees } = await getResponse.json();

          // Remove each assignee
          for (const assignee of existingAssignees || []) {
            await fetch(`/api/v1/work-items/${itemId}/assignees/${assignee.user_id}`, {
              method: 'DELETE',
              headers: { 'Authorization': `Bearer ${token}` }
            });
          }
        }
      } catch (err) {
        console.warn('[WorkItemsViewWrapper] Failed to clear assignees:', err);
      }

      // Step 2: Add new assignee if provided
      if (assignedTo) {
        try {
          await fetch(`/api/v1/work-items/${itemId}/assignees`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ user_id: assignedTo })
          });
        } catch (err) {
          console.warn('[WorkItemsViewWrapper] Failed to add assignee:', err);
        }
      }

      // Refresh items to show updated assignees
      await refreshItems();
    } catch (error) {
      console.error('[WorkItemsViewWrapper] Failed to update assignment:', error);
      throw error;
    }
  };

  // Update dates (for Timeline)
  const handleUpdateDates = async (itemId: string, startDate: string, endDate: string) => {
    if (readOnly) {
      notify.error('Schedule changes are managed from the item detail — drag editing is disabled here.');
      return;
    }
    try {
      const response = await fetch(`/api/v1/work-items/${itemId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          start_date: startDate,
          due_date: endDate
        })
      });

      if (!response.ok) throw new Error('Failed to update dates');

      // Refresh items
      await refreshItems();
    } catch (error) {
      console.error('[WorkItemsViewWrapper] Failed to update dates:', error);
      throw error;
    }
  };

  // Update due date (for Calendar)
  const handleUpdateDueDate = async (itemId: string, newDate: string) => {
    if (readOnly) {
      notify.error('Schedule changes are managed from the item detail — drag editing is disabled here.');
      return;
    }
    try {
      const response = await fetch(`/api/v1/work-items/${itemId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ due_date: newDate })
      });

      if (!response.ok) throw new Error('Failed to update due date');

      // Refresh items
      await refreshItems();
    } catch (error) {
      console.error('[WorkItemsViewWrapper] Failed to update due date:', error);
      throw error;
    }
  };

  // Create appointment for a job (extracted for use by both normal flow and location picker callback)
  // Resolved IAM Action-matrix permissions (`operations_action_permissions`) for
  // gating job -> appointment creation from calendar scheduling drops.
  const { settings: actionPermissionSettings, role: actionPermissionRole } = useWorkflowActionPermissions();

  const createAppointmentForJob = useCallback(async (
    item: WorkItem,
    scheduledStart: string,
    scheduledEnd: string,
    technicianId: number,
    headers: Record<string, string>,
    overrideLocationId?: number,
    overrideLocationAddress?: string
  ) => {
    // Calculate duration
    let duration = 1;
    if (item.estimated_hours) {
      duration = parseFloat(String(item.estimated_hours)) || 1;
    }

    // Location: use override from picker, otherwise inherit from job
    const locationId = overrideLocationId ?? item.location_id ?? null;
    const locationAddress = overrideLocationAddress ?? item.location_address ?? null;

    // Fetch fresh parent job details from DB for inheritance
    let parentCustomFields = getWorkItemCustomFields(item);
    let parentDescription: string | null = item.description || null;
    try {
      const parentRes = await fetch(`/api/v1/work-items?ids=${item.id}`, { headers });
      if (parentRes.ok) {
        const parentData = await parentRes.json() as WorkItemsByIdResponse;
        const parentItem = parentData.data?.[0];
        if (parentItem?.custom_fields) {
          parentCustomFields = parentItem.custom_fields;
        }
        if (parentItem?.description) {
          parentDescription = parentItem.description;
        }
      } else {
        console.warn('[WorkItemsViewWrapper] Failed to fetch parent job, status:', parentRes.status);
      }
    } catch (e) {
      console.warn('[WorkItemsViewWrapper] Error fetching parent job for inheritance:', e);
    }

    const appointmentPayload: Record<string, unknown> = {
      item_type: 'appointment',
      parent_id: item.id,
      title: item.title,
      description: parentDescription,
      module_context: item.module_context || moduleContext,
      assigned_to: technicianId,
      scheduled_start: scheduledStart,
      scheduled_end: scheduledEnd,
      status: 'scheduled',
      contact_id: item.contact_id || null,
      contact_name: item.contact_name || null,
      location_id: locationId,
      location_address: locationAddress,
      custom_fields: {
        require_location_verification: parentCustomFields.require_exact_location || parentCustomFields.require_location_verification || false,
        geofence_radius_meters: parentCustomFields.geofence_radius_meters || 100,
        parent_job_id: item.id,
        parent_job_number: item.item_number,
        ...(parentCustomFields.safety_requirements ? { safety_requirements: parentCustomFields.safety_requirements } : {}),
        ...(parentCustomFields.equipment_required ? { equipment_required: parentCustomFields.equipment_required } : {}),
      },
    };

    if (item.priority) appointmentPayload.priority = item.priority;
    appointmentPayload.estimated_hours = duration;

    const createResponse = await fetch('/api/v1/work-items', {
      method: 'POST',
      headers,
      body: JSON.stringify(appointmentPayload)
    });

    if (!createResponse.ok) {
      const errorText = await createResponse.text();
      console.error('[WorkItemsViewWrapper] Appointment creation failed:', errorText);
      throw new Error(`Failed to create appointment: ${errorText}`);
    }

    const appointmentData = await createResponse.json();

    // Clone subtasks from parent job to appointment
    if (appointmentData.data?.id) {
      try {
        const subtasksRes = await fetch(`/api/v1/work-items/${item.id}/subtasks`, { headers });
        if (subtasksRes.ok) {
          const subtasksData = await subtasksRes.json();
          const parentSubtasks = subtasksData.data || [];
          if (parentSubtasks.length > 0) {
            for (const subtask of parentSubtasks) {
              try {
                await fetch(`/api/v1/work-items/${appointmentData.data.id}/subtasks`, {
                  method: 'POST',
                  headers,
                  body: JSON.stringify({
                    title: subtask.title,
                    item_type: subtask.item_type || 'task',
                    status: 'pending',
                    priority: subtask.priority || 'medium',
                    description: subtask.description || null,
                  }),
                });
              } catch (stErr) {
                console.warn('[WorkItemsViewWrapper] Failed to clone subtask:', subtask.title, stErr);
              }
            }
          }
        }
      } catch (stErr) {
        console.warn('[WorkItemsViewWrapper] Failed to fetch parent subtasks:', stErr);
      }
    }

    // Update job status to scheduled
    await fetch(`/api/v1/work-items/${item.id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ status: 'scheduled' })
    });

    // Handle assignees
    const getResponse = await fetch(`/api/v1/work-items/${item.id}/assignees`, {
      method: 'GET',
      headers,
    });
    let existingAssignees: { user_id: number }[] = [];
    if (getResponse.ok) {
      const getData = await getResponse.json();
      existingAssignees = getData.data || [];
    }

    const isAlreadyAssigned = existingAssignees.some(a => a.user_id === technicianId);
    if (!isAlreadyAssigned) {
      await fetch(`/api/v1/work-items/${item.id}/assignees`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ user_id: technicianId })
      });
    }

    // Notify job thread
    const allAssigneeIds = [...existingAssignees.map(a => a.user_id)];
    if (!allAssigneeIds.includes(technicianId)) {
      allAssigneeIds.push(technicianId);
    }

    if (appointmentData.data?.id && allAssigneeIds.length > 0) {
      try {
        await fetch('/api/v1/messaging/job-threads', {
          method: 'POST',
          headers,
          body: JSON.stringify({
            action: 'notifyAppointmentScheduled',
            job_id: item.id,
            appointment_id: appointmentData.data.id,
            assignee_user_ids: allAssigneeIds,
            scheduled_start: scheduledStart,
            scheduled_end: scheduledEnd,
            appointment_title: `${item.title} - Appointment`
          })
        });
      } catch (notifyErr) {
        console.warn('[WorkItemsViewWrapper] Failed to notify job thread:', notifyErr);
      }
    }
  }, [moduleContext]);

  // Handlers for location picker in CalendarView drag-drop
  const handleCalendarLocationSelected = async (locationId: number | null, locationAddress: string | null) => {
    if (!pendingCalendarLocationPick) return;
    if (locationId === null || locationAddress === null) {
      setPendingCalendarLocationPick(null);
      return;
    }
    const { item, scheduledStart, scheduledEnd, technicianId } = pendingCalendarLocationPick;
    setPendingCalendarLocationPick(null);
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) throw new Error('Not authenticated');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      await createAppointmentForJob(item, scheduledStart, scheduledEnd, technicianId, headers, locationId, locationAddress);
      await refreshItems();
    } catch (err) {
      console.error('[WorkItemsViewWrapper] Failed to create appointment after location pick:', err);
    }
  };

  const handleCalendarLocationPickCancelled = () => {
    setPendingCalendarLocationPick(null);
  };

  // Update schedule with time slots (for Technician Timeline View)
  // When scheduling a job, create an appointment child (like Kanban does)
  // When scheduling an appointment, just update its schedule
  const handleUpdateSchedule = useCallback(async (
    itemId: string,
    scheduledStart: string,
    scheduledEnd: string,
    technicianId: number
  ) => {
    if (readOnly) {
      notify.error('Schedule changes are managed from the item detail — drag editing is disabled here.');
      return;
    }
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) {
        throw new Error('No authentication token');
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Find the item to check its type
      const item = items.find(i => i.id === itemId);
      if (!item) {
        throw new Error('Item not found');
      }

      if (item.item_type === 'job') {
        // For jobs: Create an appointment as a child (same as Kanban behavior)

        // Check if job is in a completed/closed status
        if (jobStatusBlocksAppointmentCreation(item.status)) {
          notify.error('Cannot create appointments for completed or closed jobs.');
          throw new Error('Cannot create appointments for completed or closed jobs');
        }

        // `appointment.create` for the job's workflow slice (Actions matrix)
        const appointmentWorkflowKey = (typeof item.module_context === 'string' && item.module_context.trim().length > 0)
          ? item.module_context.trim()
          : (moduleContext || templateKey || null);
        if (!isWorkflowActionPermitted({ requires_permission: 'appointment.create' }, actionPermissionSettings, actionPermissionRole, appointmentWorkflowKey)) {
          notify.error('You do not have permission to create appointments.');
          throw new Error('You do not have permission to create appointments.');
        }

        // CHECK: If job has no location set, prompt user to pick one
        if (!item.location_id) {
          setPendingCalendarLocationPick({
            item,
            scheduledStart,
            scheduledEnd,
            technicianId,
          });
          return; // Will be resumed after location selection
        }

        // Proceed with appointment creation
        await createAppointmentForJob(item, scheduledStart, scheduledEnd, technicianId, headers);

      } else {
        // For appointments: Just update the schedule and assignee

        // Update scheduled times
        const updateResponse = await fetch(`/api/v1/work-items/${itemId}`, {
          method: 'PATCH',
          headers,
          body: JSON.stringify({
            scheduled_start: scheduledStart,
            scheduled_end: scheduledEnd
          })
        });

        if (!updateResponse.ok) {
          const errorData = await updateResponse.json();
          throw new Error(errorData.error || 'Failed to update schedule');
        }

        // Update technician assignment
        // First, remove all existing assignees
        await fetch(`/api/v1/work-items/${itemId}/assignees`, {
          method: 'DELETE',
          headers
        });

        // Then add the new technician
        const assignResponse = await fetch(`/api/v1/work-items/${itemId}/assignees`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ user_id: technicianId })
        });

        if (!assignResponse.ok) {
          const errorData = await assignResponse.json();
          throw new Error(errorData.error || 'Failed to assign technician');
        }
      }

      // Refresh items
      await refreshItems();
    } catch (error) {
      console.error('[WorkItemsViewWrapper] Failed to update schedule:', error);
      throw error;
    }
  }, [actionPermissionRole, actionPermissionSettings, createAppointmentForJob, items, moduleContext, readOnly, refreshItems, templateKey]);

  // Handle create item from dropdown
  const handleCreateItem = async (itemType: string) => {
    // Call parent callback if provided
    if (onItemTypeSelect) {
      onItemTypeSelect(itemType);
    }
    // Otherwise, parent component or UniversalWorkItems handles actual creation
  };

  // Toggle item type filter (radio button behavior - only one at a time)
  const toggleItemTypeFilter = useCallback((typeId: string) => {
    setSelectedItemTypes(prev =>
      prev.includes(typeId) && prev.length === 1
        ? [] // If clicking the only selected item, clear all (show all)
        : [typeId] // Otherwise, select only this item
    );
  }, []);

  // Handle status filter tab change (for finance mode)
  const handleStatusFilterChange = useCallback((status: string | null) => {
    setSelectedStatusFilter(status);
    if (onStatusFilterChange) {
      onStatusFilterChange(status);
    }
  }, [onStatusFilterChange]);

  useEffect(() => {
    if (statusFilterOptions.length === 0) {
      if (selectedStatusFilter !== null) {
        setSelectedStatusFilter(null);
        onStatusFilterChange?.(null);
      }
      return;
    }

    if (!selectedStatusFilter) {
      // In finance mode, default to the first status pill instead of
      // showing all statuses (the "All" tab was removed from finance).
      if (financeMode) {
        const firstValid = statusFilterOptions.find((o) => o.value && o.value !== 'all');
        if (firstValid) {
          setSelectedStatusFilter(firstValid.value);
          onStatusFilterChange?.(firstValid.value);
        }
      }
      return;
    }

    const validStatuses = new Set(
      statusFilterOptions
        .map((option) => option.value)
        .filter((value) => value && value !== 'all')
    );

    if (!validStatuses.has(selectedStatusFilter)) {
      setSelectedStatusFilter(null);
      onStatusFilterChange?.(null);
    }
  }, [onStatusFilterChange, selectedStatusFilter, statusFilterOptions, financeMode]);

  // Calculate status counts for tabs (for finance mode)
  const clientStatusCounts = useMemo(() => {
    if (financeMode && financeListDocumentType === 'invoice' && statusFilterOptions.length > 0) {
      const counts: Record<string, number> = { all: items.length };
      statusFilterOptions.forEach((option) => {
        if (option.value === 'all') {
          return;
        }
        counts[option.value] = items.filter((it) =>
          invoiceMatchesListStatusTab(it as never, option.value)
        ).length;
      });
      return counts;
    }
    const counts: Record<string, number> = { all: items.length };
    items.forEach((item) => {
      const status = item.status || 'unknown';
      counts[status] = (counts[status] || 0) + 1;
    });
    return counts;
  }, [financeListDocumentType, financeMode, items, statusFilterOptions]);

  const resolvedStatusCounts = useMemo(() => {
    return serverStatusCounts
      ? { all: Object.values(serverStatusCounts).reduce((sum, count) => sum + count, 0), ...serverStatusCounts }
      : clientStatusCounts;
  }, [clientStatusCounts, serverStatusCounts]);

  const workItemsRealtimeStatus: 'live' | 'online' | 'offline' = wsConnectionStatus === 'connected'
    ? 'live'
    : isNetworkOnline
      ? 'online'
      : 'offline';

  const workItemsRealtimeIndicator = useMemo(() => {
    const dotClass = workItemsRealtimeStatus === 'live'
      ? 'bg-green-500'
      : workItemsRealtimeStatus === 'online'
        ? 'bg-amber-400'
        : 'bg-gray-400';
    const label = workItemsRealtimeStatus === 'live'
      ? 'Live'
      : workItemsRealtimeStatus === 'online'
        ? 'Online'
        : 'Offline';

    return (
      <div className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-medium text-white/90 backdrop-blur-sm">
        <span className={`h-1.5 w-1.5 rounded-full ${dotClass}`} />
        <span>{label}</span>
      </div>
    );
  }, [workItemsRealtimeStatus]);

  const headerStatusFilterContent = useMemo(() => {
    if (statusFilterOptions.length === 0 || !showHeader || !headerTitle) {
      return null;
    }

    const colorClasses: Record<string, string> = {
      gray: 'text-gray-600 dark:text-gray-400',
      yellow: 'text-yellow-600 dark:text-yellow-400',
      blue: 'text-blue-600 dark:text-blue-400',
      green: 'text-green-600 dark:text-green-400',
      red: 'text-red-600 dark:text-red-400',
      purple: 'text-purple-600 dark:text-purple-400',
      orange: 'text-orange-600 dark:text-orange-400',
      teal: 'text-teal-600 dark:text-teal-400',
    };

    return (
      <div className="flex justify-center overflow-x-auto">
        <div className="flex items-center justify-center gap-1 sm:gap-1.5 flex-nowrap rounded-lg bg-white/10 p-0.5 scrollbar-thin scrollbar-thumb-white/20">
          {statusFilterOptions.map((option) => {
            const isActive = selectedStatusFilter === option.value || (option.value === 'all' && !selectedStatusFilter);
            const count = resolvedStatusCounts[option.value] || 0;
            const Icon = option.icon;

            return (
              <button
                key={option.value}
                onClick={() => handleStatusFilterChange(option.value === 'all' ? null : option.value)}
                className={[
                  'flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] sm:text-xs font-medium whitespace-nowrap transition-colors',
                  isActive
                    ? 'bg-white text-slate-800 shadow-sm'
                    : 'text-white/90 hover:bg-white/15'
                ].join(' ')}
              >
                {Icon && (
                  <Icon
                    className={[
                      'h-3 w-3 sm:h-3.5 sm:w-3.5',
                      isActive ? 'text-current' : (option.color ? colorClasses[option.color] : 'text-white/80')
                    ].join(' ')}
                  />
                )}
                <span>{option.label}</span>
                <span
                  className={[
                    'rounded-full px-1.5 py-0.5 text-[10px] font-semibold',
                    isActive
                      ? 'bg-slate-100 text-slate-600'
                      : 'bg-white/15 text-white/80'
                  ].join(' ')}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }, [handleStatusFilterChange, headerTitle, resolvedStatusCounts, selectedStatusFilter, showHeader, statusFilterOptions]);

  // Derive available item types from loaded items for filter dropdown
  const derivedItemTypes = useMemo(() => {
    const types = new Set(items.map(item => item.item_type).filter(Boolean));
    return Array.from(types) as string[];
  }, [items]);

  // Derive available item subtypes (service_call, maintenance, inspection, etc.) for filter dropdown
  const derivedItemSubtypes = useMemo(() => {
    const subtypes = new Set(items.map(item => item.item_subtype).filter(Boolean));
    return Array.from(subtypes) as string[];
  }, [items]);

  // Build centerContent for header: item type filters (like Email module toggles) or headerActions
  const headerCenterContent = useMemo(() => {
    let mainContent: React.ReactNode | undefined;

    // Item type filters take priority if enabled
    if (enableItemTypeFilter && itemTypeOptions.length > 0 && !hideFilters && viewMode !== 'roadmap') {
      mainContent = (
        <div className="flex items-center gap-0 rounded-lg bg-white/10 p-0.5 overflow-x-auto scrollbar-thin">
          {showAllFilter && (
            <button
              onClick={() => setSelectedItemTypes([])}
              className={`flex items-center justify-center gap-1 px-2.5 py-1 rounded-md font-medium text-[13px] whitespace-nowrap transition-colors ${selectedItemTypes.length === 0
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-white/90 hover:bg-white/20'
                }`}
            >
              All
            </button>
          )}
          {orderedItemTypeOptions.map(option => (
            <button
              key={option.id}
              onClick={() => toggleItemTypeFilter(option.id)}
              className={`flex items-center justify-center gap-1 px-2.5 py-1 rounded-md font-medium text-[13px] whitespace-nowrap transition-colors ${selectedItemTypes.includes(option.id)
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-white/90 hover:bg-white/20'
                }`}
            >
              {React.createElement(option.icon, { className: 'h-3.5 w-3.5 flex-shrink-0' })}
              <span className="hidden sm:inline">{option.label}</span>
            </button>
          ))}
          {customFilterButtons && customFilterButtons.map(btn => (
            <button
              key={btn.id}
              onClick={btn.onClick}
              className="flex items-center justify-center gap-1 px-2.5 py-1 rounded-md font-medium text-[13px] whitespace-nowrap text-white/90 hover:bg-white/20 transition-colors"
              title={btn.description}
            >
              {React.createElement(btn.icon, { className: 'h-3.5 w-3.5 flex-shrink-0' })}
              <span className="hidden sm:inline">{btn.label}</span>
            </button>
          ))}
        </div>
      );
    } else if (headerActions) {
      // Fall back to headerActions if provided
      mainContent = (
        <div className="flex items-center gap-2">
          {headerActions}
        </div>
      );
    }

    // "Agent" pill is orthogonal to item-type filters — show wherever a
    // filter surface exists, alongside type pills or on its own.
    if (!hideFilters && viewMode !== 'roadmap') {
      const agentPill = (
        <button
          key="agent-filter"
          onClick={() => setAgentOnly((v) => !v)}
          className={`flex items-center justify-center gap-1 px-2.5 py-1 rounded-md font-medium text-[13px] whitespace-nowrap transition-colors ${agentOnly
            ? 'bg-white text-blue-600 shadow-sm'
            : 'bg-white/10 text-white/90 hover:bg-white/20'
            }`}
          title="Show only items the agent has worked on"
        >
          <Bot className="h-3.5 w-3.5 flex-shrink-0" />
          <span className="hidden sm:inline">Agent</span>
        </button>
      );
      mainContent = (
        <div className="flex items-center gap-1.5">
          {agentPill}
          {mainContent}
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2">
        {mainContent}
        {workItemsRealtimeIndicator}
      </div>
    );
  }, [agentOnly, customFilterButtons, enableItemTypeFilter, headerActions, hideFilters, itemTypeOptions, orderedItemTypeOptions, selectedItemTypes, showAllFilter, toggleItemTypeFilter, viewMode, workItemsRealtimeIndicator]);

  const resolvedHeaderStatCards = useMemo(() => {
    return typeof statsCards === 'function'
      ? statsCards(items as WorkItem[])
      : statsCards;
  }, [items, statsCards]);

  // Publish resolved stat cards to a parent StrictModuleHeader when this
  // wrapper's own header is suppressed by an embedded custom view.
  useEffect(() => {
    if (!onStatsChange) return;
    onStatsChange(
      (resolvedHeaderStatCards ?? []).map((stat, index) => ({
        id: `wi-stat-${index}`,
        label: stat.label,
        value: stat.value,
        icon: stat.icon,
      })),
    );
  }, [onStatsChange, resolvedHeaderStatCards]);

  // View mode toggle as StrictModuleHeader Row D filter pills (opt-in via
  // viewToggleInHeader). Mirrors the enabled-mode checks of the inline toggle.
  const viewModeFilterPills = useMemo<StrictFilterPill[]>(() => {
    if (!viewToggleInHeader || !strictHeaderProps) return [];
    const pills: StrictFilterPill[] = [];
    const addPill = (mode: ViewMode, label: string, icon: React.ElementType) => {
      pills.push({
        id: `view-mode-${mode}`,
        label,
        icon,
        active: viewMode === mode,
        onClick: () => setViewMode(mode),
        dataTourId: mode === 'list' ? 'work-items-view-toggles' : undefined,
      });
    };
    if (!enabledViewModes || enabledViewModes.includes('list')) addPill('list', 'List', List);
    if (!enabledViewModes || enabledViewModes.includes('tree')) addPill('tree', 'Tree', Workflow);
    if (!enabledViewModes || enabledViewModes.includes('gantt')) addPill('gantt', 'Gantt', GanttIcon);
    if (!enabledViewModes || enabledViewModes.includes('board')) addPill('board', 'Board', LayoutGrid);
    if (!enabledViewModes || enabledViewModes.includes('calendar')) addPill('calendar', 'Calendar', CalendarIcon);
    if (isSlaViewEnabled) addPill('sla', 'SLA', Target);
    if (enabledViewModes?.includes('backlog')) addPill('backlog', 'Backlog', List);
    if (enabledViewModes?.includes('sprint')) addPill('sprint', 'Sprint', Zap);
    if (enabledViewModes?.includes('roadmap')) addPill('roadmap', 'Roadmap', GanttIcon);
    return pills;
  }, [viewToggleInHeader, strictHeaderProps, enabledViewModes, isSlaViewEnabled, viewMode]);

  const isViewToggleInHeader = viewToggleInHeader && Boolean(strictHeaderProps);

  // Build resolved strict header props by merging the caller's strictHeaderProps
  // with wrapper-computed stat cards and status filter content.
  const resolvedStrictHeaderProps = useMemo<StrictModuleHeaderProps | null>(() => {
    if (!strictHeaderProps) return null;
    // Map wrapper-computed stat cards to StrictStatCard[]
    const wrapperStatCards: StrictStatCard[] | undefined = resolvedHeaderStatCards?.map((stat, index) => ({
      id: `wi-stat-${index}`,
      label: stat.label,
      value: stat.value,
      icon: stat.icon,
    }));
    // Merge: caller's statCards take precedence, otherwise use wrapper's
    const mergedStatCards = strictHeaderProps.statCards ?? wrapperStatCards;
    // Merge filtersSlot: caller's + wrapper's status filter content
    const mergedFiltersSlot = (
      <>
        {strictHeaderProps.filtersSlot}
        {headerStatusFilterContent}
      </>
    );
    // Merge filter pills: view-mode pills first, then caller's (first caller
    // pill gets a visual separator so the groups read as distinct)
    const callerFilterPills = strictHeaderProps.filterPills ?? [];
    const mergedFilterPills = viewModeFilterPills.length > 0
      ? [
          ...viewModeFilterPills,
          ...callerFilterPills.map((pill, index) => (
            index === 0 ? { ...pill, separatorBefore: true } : pill
          )),
        ]
      : strictHeaderProps.filterPills;
    return {
      ...strictHeaderProps,
      statCards: mergedStatCards,
      filtersSlot: mergedFiltersSlot,
      filterPills: mergedFilterPills,
      // Ensure showNewButton/onNew are set from wrapper if not provided by caller
      showNewButton: strictHeaderProps.showNewButton ?? Boolean(onCreateItem && permissions.can_create),
      newButtonLabel: strictHeaderProps.newButtonLabel ?? createButtonLabel,
      onNew: strictHeaderProps.onNew ?? onCreateItem,
      newButtonDataTourId: strictHeaderProps.newButtonDataTourId ?? createButtonDataTourId,
      showCombinedLayoutToggle: strictHeaderProps.showCombinedLayoutToggle ?? true,
      favouriteConfig: strictHeaderProps.favouriteConfig ?? headerFavouriteConfig ?? undefined,
    };
  }, [strictHeaderProps, resolvedHeaderStatCards, headerStatusFilterContent, viewModeFilterPills, onCreateItem, permissions.can_create, createButtonLabel, createButtonDataTourId, headerFavouriteConfig]);

  const shouldUseManagedBoardViewport = isScheduleView;

  const handleFilterBarExport = useCallback(async (
    records: Array<Record<string, unknown>>,
    fileName: string,
    format: ExportFormat = 'csv',
  ) => {
    const accessToken = authService.getTokens()?.access_token;
    if (!accessToken) {
      notify.error('Your session expired before the export could include child appointments.');
      return;
    }

    try {
      const exportRows = await enrichJobExportRecordsWithChildAppointments(records, accessToken);
      switch (format) {
        case 'json':
          downloadRecordsAsJson(exportRows, fileName);
          break;
        case 'xlsx':
          downloadRecordsAsXlsx(exportRows, fileName, exportColumnDefs);
          break;
        case 'csv':
        default:
          downloadRecordsAsCsv(exportRows, fileName);
          break;
      }
    } catch (error) {
      console.error('[WorkItemsViewWrapper] Failed to export child appointments:', error);
      notify.error(error instanceof Error ? error.message : 'Failed to include child appointments in the export');
      throw error;
    }
  }, [exportColumnDefs]);

  const shouldStickFilterBar = viewMode === 'list' || viewMode === 'board' || viewMode === 'tree';
  const shouldRenderStatusFilterTabs = statusFilterOptions.length > 0 && (!showHeader || !headerTitle);
  const shouldRenderFilterBar = showFilterBar && !hideFilters;
  const shouldUsePersistentTopChrome = shouldStickFilterBar && (shouldRenderStatusFilterTabs || shouldRenderFilterBar);

  const statusFilterTabsContent = shouldRenderStatusFilterTabs ? (
    <div className={embeddedStatusTabs ? 'relative overflow-hidden rounded-xl border border-gray-200 bg-white/90 p-1 dark:border-gray-700/70 dark:bg-gray-900/80' : 'overflow-x-auto'}>
      <div className={embeddedStatusTabs ? 'scrollbar-hide flex snap-x snap-mandatory items-center gap-1 overflow-x-auto' : 'flex items-center gap-1 sm:gap-2 border-b border-gray-200 dark:border-gray-700 pb-2'}>
        {statusFilterOptions.map(option => {
          const isActive = selectedStatusFilter === option.value || (option.value === 'all' && !selectedStatusFilter);
          const count = resolvedStatusCounts[option.value] || 0;
          const Icon = option.icon;

          // Color mapping for tab styling
          const colorClasses: Record<string, string> = {
            gray: 'text-gray-600 dark:text-gray-400',
            yellow: 'text-yellow-600 dark:text-yellow-400',
            blue: 'text-blue-600 dark:text-blue-400',
            green: 'text-green-600 dark:text-green-400',
            red: 'text-red-600 dark:text-red-400',
            purple: 'text-purple-600 dark:text-purple-400',
            orange: 'text-orange-600 dark:text-orange-400',
            teal: 'text-teal-600 dark:text-teal-400',
          };

          return (
            <button
              key={option.value}
              onClick={() => handleStatusFilterChange(option.value === 'all' ? null : option.value)}
              className={embeddedStatusTabs
                ? `shrink-0 snap-start flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-xs font-medium whitespace-nowrap transition-all sm:px-3 sm:text-sm ${isActive ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-800 dark:text-white' : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'}`
                : `flex items-center gap-1.5 px-3 py-2 rounded-t-lg text-sm font-medium whitespace-nowrap transition-colors ${isActive ? 'bg-white dark:bg-gray-800 border-b-2 border-blue-500 text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
              style={embeddedStatusTabs && isActive ? { color: primaryColor } : undefined}
            >
              {Icon && <Icon className={`w-4 h-4 ${isActive ? 'text-current' : option.color ? colorClasses[option.color] : ''}`} />}
              <span>{option.label}</span>
              <span
                className={`text-xs px-1.5 py-0.5 rounded-full ${embeddedStatusTabs
                  ? (isActive
                    ? 'bg-gray-100 dark:bg-gray-700'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400')
                  : (isActive
                    ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400')
                  }`}
                style={embeddedStatusTabs && isActive ? { backgroundColor: `${primaryColor}1A`, color: primaryColor } : undefined}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  ) : null;

  const filterBarContent = shouldRenderFilterBar ? (
    <div data-tour-id="work-items-filter-bar">
      <WorkItemsFilterBar
        filterState={filterState}
        sticky={!shouldUsePersistentTopChrome && shouldStickFilterBar}
        statsSummaryOverride={filterBarStatsSummary}
        className="rounded-none border-x-0 border-t-0 px-0 py-0 shadow-none"
        compact={filterBarCompact || isMobile}
        singleRow={filterBarSingleRow}
        showStats={showFilterBarStats && !isMobile}
        showQuickFilters={showFilterBarQuickFilters}
        showPresets={showFilterBarPresets && !isMobile}
        showSortControls={showFilterBarSortControls}
        showStatusFilter={showFilterBarStatusFilter}
        showPriorityFilter={showFilterBarPriorityFilter}
        showAssigneeFilter={showFilterBarAssigneeFilter}
        showTypeFilter={showFilterBarTypeFilter}
        showDateFilter={showFilterBarDateFilter}
        availableStatusOptions={filterBarStatusOptions}
        availableAssignees={mergedAssignees}
        hideAssigneeSelectAll={hideAssigneeSelectAll}
        attentionCount={attentionCount}
        onAttentionCountClick={onAttentionCountClick}
        mobileTabToggle={mobileTabToggle}
        customSlot={filterBarCustomSlot}
        availableTags={availableTags}
        availableTypes={derivedItemTypes}
        availableSubtypes={derivedItemSubtypes}
        placeholder={workItemType ? `Search ${workItemType}s...` : 'Search items...'}
        showContactFilter={showContactFilter}
        selectedContact={selectedContact}
        onContactChange={setSelectedContact}
        contactTypes={contactFilterTypes}
        availableContacts={availableContacts}
        stylePreset={kanbanStylePreset}
        activeItemType={focusedWorkflowItemType}
        activeItemTypeLabel={activeItemTypeLabel}
        primaryColor={primaryColor}
        exportFileName={activeItemTypeLabel || workItemType || 'work-items'}
        onExport={handleFilterBarExport}
        exportColumns={exportColumnDefs}
        statusCountsOverride={serverStatusCounts ?? undefined}
        onClearHeaderStatFilter={activeHeaderStatFilter ? () => onActiveHeaderStatFilterChange?.(null) : undefined}
        showFinanceRecurringQuickFilter={showFinanceRecurringQuickFilter}
      />
      {isBackendFilterPending && (
        <div className="px-0 py-1 text-xs text-blue-600 dark:text-blue-400">
          Showing instant matches while filtering all records…
        </div>
      )}
    </div>
  ) : null;

  const persistentTopChrome = shouldUsePersistentTopChrome ? (
    <>
      {filterBarPrefix}
      {statusFilterTabsContent}
      {filterBarContent}
    </>
  ) : (filterBarPrefix ? <>{filterBarPrefix}</> : null);

  const forwardedKanbanBoardProps = useMemo(() => buildWorkItemsKanbanForwardedProps({
    boardKey,
    listViewBoardKey,
    workflowStates: effectiveWorkflowStates,
    resolvedModuleKey,
    assignedUsersOverride: kanbanAssignedUsers,
    currentUserId,
    workflowField,
    workspaceId,
    enableTimeAllocation,
    stylePreset: kanbanStylePreset,
    columnColorOverrides: kanbanColumnColors,
    preferenceReloadKey,
    onColumnsExposed,
    onUpdateSchedule: handleUpdateSchedule,
    defaultBoardViewMode,
    hideBoardViewToggle,
    onAppointmentChange,
    onNavigateToRoutes,
    activeViewLabel: propActiveViewLabel,
    onOpenBoardPreferences,
    onOpenTeamMember: openTeamMember,
    hideBoardToolbar,
    onToolbarModelChange: setKanbanToolbarModel,
    filtersActive: filterState.hasActiveFilters,
    activeAssigneeUserIds,
    onScheduleDateChange: setScheduleSelectedDate,
  }), [
    boardKey,
    currentUserId,
    defaultBoardViewMode,
    effectiveWorkflowStates,
    enableTimeAllocation,
    handleUpdateSchedule,
    hideBoardToolbar,
    hideBoardViewToggle,
    kanbanAssignedUsers,
    kanbanColumnColors,
    kanbanStylePreset,
    listViewBoardKey,
    onAppointmentChange,
    onColumnsExposed,
    onNavigateToRoutes,
    onOpenBoardPreferences,
    openTeamMember,
    preferenceReloadKey,
    propActiveViewLabel,
    resolvedModuleKey,
    workflowField,
    workspaceId,
    filterState.hasActiveFilters,
    activeAssigneeUserIds,
    setScheduleSelectedDate,
  ]);

  return (
    <ModulePageShell
      filterBar={persistentTopChrome}
      filterBarClassName={shouldUsePersistentTopChrome ? (embeddedStatusTabs ? 'px-2.5 pt-1 sm:px-3 sm:pt-1.5' : 'px-2.5 pt-2.5 sm:px-3 sm:pt-3') : undefined}
      showSurface={showHeader && Boolean(headerTitle)}
      strictHeaderProps={resolvedStrictHeaderProps}
      headerProps={!resolvedStrictHeaderProps && showHeader && headerTitle ? {
        variant: resolvedHeaderStatCards && resolvedHeaderStatCards.length > 0 ? 'with-stats' : 'compact',
        density: moduleHeaderProps?.density ?? 'compact',
        icon: headerIcon || Plus,
        title: headerTitle,
        subtitle: headerSubtitle,
        centerContent: headerCenterContent,
        statCards: resolvedHeaderStatCards?.map((stat, index) => ({
          id: `wi-stat-${index}`,
          label: stat.label,
          value: stat.value,
          subtitle: stat.sublabel,
          icon: stat.icon,
        })),
        showNewButton: Boolean(onCreateItem && permissions.can_create),
        newButtonLabel: createButtonLabel,
        onNew: onCreateItem,
        newButtonDataTourId: createButtonDataTourId,
        ...moduleHeaderProps,
        persistentBottomSlot: (
          <>
            {moduleHeaderProps?.persistentBottomSlot}
            {headerStatusFilterContent}
          </>
        ),
        showCombinedLayoutToggle: moduleHeaderProps?.showCombinedLayoutToggle ?? true,
        primaryColor: moduleHeaderProps?.primaryColor ?? primaryColor,
        storageKey: moduleHeaderProps?.storageKey ?? `ta_wi_header_${workItemType || 'default'}`,
        // Applied last: favourites must survive the wrapper/base merge (moduleHeaderProps may omit or be partial in some shells)
        favouriteConfig: headerFavouriteConfig ?? moduleHeaderProps?.favouriteConfig,
      } : null}
      contentClassName={flushContent
        ? (shouldUseManagedBoardViewport ? 'h-full min-h-0 overflow-hidden flex flex-col' : '')
        : shouldUseManagedBoardViewport
          ? `h-full min-h-0 overflow-hidden flex flex-col ${shouldUsePersistentTopChrome ? 'px-2.5 pb-2.5 pt-0 sm:px-3 sm:pb-3 sm:pt-0' : 'p-2.5 sm:p-3'}`
          : (shouldUsePersistentTopChrome ? 'px-2.5 pb-2.5 pt-0 sm:px-3 sm:pb-3 sm:pt-0' : 'p-2.5 sm:p-3')
      }
    >

      {/* Filter bar prefix (e.g. intake email banner) — below header, above filters */}
      {!shouldUsePersistentTopChrome && filterBarPrefix}

      {/* Status Filter Tabs (Finance Mode) */}
      {!shouldUsePersistentTopChrome && statusFilterTabsContent}

      {/* NEW: Comprehensive Filter Bar (December 2025) - Mobile Responsive */}
      {!shouldUsePersistentTopChrome && filterBarContent}

      {/* Create Dropdown (Item type filters moved to header) */}
      {!hideFilters && showCreateDropdown && viewMode !== 'roadmap' && (
        <div className="flex items-center justify-end">
          {showCreateDropdown && permissions.can_create && itemTypeOptions.length > 0 && (
            <WorkItemCreateDropdown
              buttonLabel="New Item"
              buttonGradient="from-blue-500 to-indigo-600"
              options={itemTypeOptions}
              onSelect={handleCreateItem}
            />
          )}
        </div>
      )}

      {/* Item Type Filters - shown below when header is hidden */}
      {!showHeader && !hideFilters && enableItemTypeFilter && itemTypeOptions.length > 0 && viewMode !== 'roadmap' && (
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
          {showAllFilter && (
            <button
              onClick={() => setSelectedItemTypes([])}
              className={`px-2.5 py-1 rounded-lg text-sm font-medium transition-colors ${selectedItemTypes.length === 0
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
            >
              All
            </button>
          )}
          {orderedItemTypeOptions.map(option => (
            <button
              key={option.id}
              onClick={() => toggleItemTypeFilter(option.id)}
              className={`px-2.5 py-1 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${selectedItemTypes.includes(option.id)
                ? `${option.color} text-white`
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
            >
              {React.createElement(option.icon, { className: 'w-4 h-4' })}
              {option.label}
            </button>
          ))}
        </div>
      )}

      {/* View Mode Toggle - Compact */}
      {!hideViewToggle && !isViewToggleInHeader && (
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-1.5">
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0">
            <span className="text-xs font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">View:</span>
            <UserDocsLink docsPath="/total-operations/workflows/service-operations/views" label="User guide: Views & boards" />
            <div className="flex gap-0.5 bg-gray-100 dark:bg-gray-700 rounded-lg p-0.5" data-tour-id="work-items-view-toggles">
              {(!enabledViewModes || enabledViewModes.includes('list')) && (
              <button
                onClick={() => setViewMode('list')}
                className={`
                flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors whitespace-nowrap touch-manipulation
                ${viewMode === 'list'
                    ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }
              `}
              >
                <List className="h-3.5 w-3.5" />
                {!isMobile && 'List'}
              </button>
              )}
              {(!enabledViewModes || enabledViewModes.includes('tree')) && (
              <button
                onClick={() => setViewMode('tree')}
                className={`
                flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors whitespace-nowrap touch-manipulation
                ${viewMode === 'tree'
                    ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }
              `}
              >
                <Workflow className="h-3.5 w-3.5" />
                {!isMobile && 'Tree'}
              </button>
              )}
              {(!enabledViewModes || enabledViewModes.includes('gantt')) && (
              <button
                onClick={() => setViewMode('gantt')}
                className={`
                flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors whitespace-nowrap touch-manipulation
                ${viewMode === 'gantt'
                    ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }
              `}
              >
                <GanttIcon className="h-3.5 w-3.5" />
                {!isMobile && 'Gantt'}
              </button>
              )}
              {(!enabledViewModes || enabledViewModes.includes('board')) && (
              <button
                onClick={() => setViewMode('board')}
                className={`
                flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors whitespace-nowrap touch-manipulation
                ${viewMode === 'board'
                    ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }
              `}
              >
                <LayoutGrid className="h-3.5 w-3.5" />
                {!isMobile && 'Board'}
              </button>
              )}
              {(!enabledViewModes || enabledViewModes.includes('calendar')) && (
              <button
                onClick={() => setViewMode('calendar')}
                className={`
                flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors whitespace-nowrap touch-manipulation
                ${viewMode === 'calendar'
                    ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }
              `}
              >
                <CalendarIcon className="h-3.5 w-3.5" />
                {!isMobile && 'Calendar'}
              </button>
              )}
              {isSlaViewEnabled && (
                <button
                  onClick={() => setViewMode('sla')}
                  className={`
                  flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors whitespace-nowrap touch-manipulation
                  ${viewMode === 'sla'
                      ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                    }
                `}
                >
                  <Target className="h-3.5 w-3.5" />
                  {!isMobile && 'SLA'}
                </button>
              )}
              {enabledViewModes?.includes('backlog') && (
                <button
                  onClick={() => setViewMode('backlog')}
                  className={`
                  flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors whitespace-nowrap touch-manipulation
                  ${viewMode === 'backlog'
                      ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                    }
                `}
                >
                  <List className="h-3.5 w-3.5" />
                  {!isMobile && 'Backlog'}
                </button>
              )}
              {enabledViewModes?.includes('sprint') && (
                <button
                  onClick={() => setViewMode('sprint')}
                  className={`
                  flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors whitespace-nowrap touch-manipulation
                  ${viewMode === 'sprint'
                      ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                    }
                `}
                >
                  <Zap className="h-3.5 w-3.5" />
                  {!isMobile && 'Sprint'}
                </button>
              )}
              {enabledViewModes?.includes('roadmap') && (
                <button
                  onClick={() => setViewMode('roadmap')}
                  className={`
                  flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors whitespace-nowrap touch-manipulation
                  ${viewMode === 'roadmap'
                      ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                    }
                `}
                >
                  <GanttIcon className="h-3.5 w-3.5" />
                  {!isMobile && 'Roadmap'}
                </button>
              )}
            </div>
          </div>

          {/* Item count */}
          {viewMode !== 'list' && filteredItems.length > 0 && (
            <div className="text-xs text-gray-500 dark:text-gray-400 text-center sm:text-right">
              {filteredItems.length} items {viewMode === 'tree' && `(${treeData.length} root)`}
            </div>
          )}
        </div>
      )}

      {/* List View - Data Grid */}
      {viewMode === 'list' && (
        <div className="min-w-0 w-full max-w-full" data-tour-id="work-items-list-view">
          <WorkItemsDataGrid
            items={dataGridItems}
            selectedItems={selectedItems}
            onToggleSelection={handleToggleSelection}
            onSelectAll={handleSelectAll}
            onClearSelection={handleClearSelection}
            onViewItem={handleViewItem}
            loading={loading}
            viewMode="list"
            itemTypeOptions={dataGridItemTypeOptions}
            financeMode={financeMode}
            financeDocumentType={financeListDocumentType}
            showContactAvatar={showContactAvatar}
            personalMode={isPersonalMode}
            stylePreset={kanbanStylePreset}
            onOpenTeamMember={openTeamMember}
            activeItemType={focusedWorkflowItemType}
            activeItemTypeLabel={activeItemTypeLabel}
            primaryColor={primaryColor}
            visibleColumns={persistedListVisibleColumns}
            onVisibleColumnsChange={handleListVisibleColumnsChange}
            boardKey={listViewBoardKey}
            persistItemType={listViewPersistItemType}
            inventoryMode={effectiveInventoryMode}
            showDeletedFilter={canViewDeletedItems}
            deletedFilter={deletedFilterMode}
            onDeletedFilterChange={setDeletedFilterMode}
            onRestoreItem={canViewDeletedItems ? handleRestoreItem : undefined}
            hideColumnSettings={hideBoardToolbar}
            onOpenBoardPreferences={onOpenBoardPreferences}
            styleVariant={dataGridStyleVariant}
            serverPagination={pagination ? {
              total: pagination.total,
              page: listGridPage,
              pageSize: listGridPageSize,
              onPageChange: setListGridPage,
              onPageSizeChange: (size) => {
                setListGridPageSize(size);
                setListGridPage(1);
                setListPageCursors({});
              },
            } : undefined}
            renderCustomActions={renderListRowExtraActions}
            renderRowThumbnail={renderRowThumbnail}
            customEmptyState={emptyStateConfig}
            swipeActions={swipeActions as never}
          />
        </div>
      )}

      {/* Tree View - Hierarchical WorkItemTree */}
      {viewMode === 'tree' && (
        <WorkItemTree
          items={treeData}
          onViewItem={handleTreeItemClick}
          onAddChild={handleAddChild}
          showProgress={resolvedTreeVisibleColumns.includes('progress')}
          showMetrics={resolvedTreeVisibleColumns.includes('progress')}
          showAssignees={resolvedTreeVisibleColumns.includes('assignees')}
          showContact={!!showContactAvatar}
          showSLA={true}
          showType={resolvedTreeVisibleColumns.includes('type')}
          showStatus={resolvedTreeVisibleColumns.includes('status')}
          showActions={resolvedTreeVisibleColumns.includes('actions')}
          loading={loading}
          stylePreset={kanbanStylePreset}
          onOpenTeamMember={openTeamMember}
          activeItemType={focusedWorkflowItemType}
          activeItemTypeLabel={activeItemTypeLabel}
          primaryColor={primaryColor}
          onOpenBoardPreferences={onOpenBoardPreferences}
        />
      )}

      {/* Gantt View - Timeline Chart (using WorkItemsTimelineView) */}
      {viewMode === 'gantt' && (
        <div>
          <WorkItemsTimelineView
            items={filteredItems as WorkItem[]}
            onUpdateDates={handleUpdateDates}
            onViewItem={handleViewItem}
            loading={loading}
            stylePreset={kanbanStylePreset}
            activeItemType={focusedWorkflowItemType}
            activeItemTypeLabel={activeItemTypeLabel}
            primaryColor={primaryColor}
          />
        </div>
      )}

      {/* Board View - Kanban (using WorkItemsKanbanBoard) */}
      {viewMode === 'board' && (
        <div className={shouldUseManagedBoardViewport
          ? 'flex min-h-0 flex-1 flex-col overflow-hidden'
          : 'flex h-[calc(100vh-12rem)] min-h-0 flex-col overflow-hidden'} data-tour-id="work-items-kanban-board">
          {loading && filteredItems.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8">
              <SectionLoader
                size="sm"
                primaryColor={primaryColor || '#2563eb'}
                message="Loading board..."
              />
            </div>
          ) : (
            <>
              <WorkItemsKanbanBoard
                items={itemsForKanban as WorkItem[]}
                onUpdateStatus={handleUpdateStatus}
                onUpdateAssignment={handleUpdateAssignment}
                onViewItem={handleViewItem}
                onRefresh={refreshItems}
                itemType={workItemType || focusedWorkflowItemType || primaryItemType || 'work_item'}
                focusedItemType={focusedWorkflowItemType}
                workflowStatuses={loadedWorkflowStatuses}
                workflowContext={moduleContext}
                {...forwardedKanbanBoardProps}
              />
            </>
          )}
        </div>
      )}

      {/* Calendar View - Calendar with drag-to-reschedule */}
      {viewMode === 'calendar' && (
        <div>
          <WorkItemsCalendarView
            items={itemsForKanban as WorkItem[]}
            onUpdateDueDate={handleUpdateDueDate}
            onUpdateSchedule={handleUpdateSchedule}
            readOnly={readOnly}
            onViewItem={handleViewItem}
            loading={loading}
            stylePreset={kanbanStylePreset}
            activeItemType={focusedWorkflowItemType}
            activeItemTypeLabel={activeItemTypeLabel}
            primaryColor={primaryColor}
          />
        </div>
      )}

      {/* SLA View - compliance, breach risk, and deadline tracking */}
      {viewMode === 'sla' && (
        <WorkItemsSLA
          items={filteredItems as WorkItem[]}
          loading={loading}
          onViewItem={(item) => {
            void handleViewItem(item);
          }}
          activeItemType={focusedWorkflowItemType}
          activeItemTypeLabel={activeItemTypeLabel}
          primaryColor={primaryColor}
          onConfigureSLA={onConfigureSla}
        />
      )}

      {/* Advanced Views - Available for any template that configures them */}

      {/* Backlog View - Sprint planning, epic grouping, prioritization */}
      {viewMode === 'backlog' && (
        <ProductBacklogView
          permissions={{ can_view: true, can_create: true, can_edit: true, can_delete: true, can_admin: false }}
          items={filteredItems as WorkItem[]}
        />
      )}

      {/* Sprint View - Active sprint with burndown, velocity, capacity */}
      {viewMode === 'sprint' && (
        <SprintView
          permissions={{ can_view: true, can_create: true, can_edit: true, can_delete: true, can_admin: false }}
          currentUserId={currentUserId}
          filterByAssignedUser={filterByAssignedUser}
        />
      )}

      {/* Roadmap View - Quarter-based timeline with epics, milestones, sprints */}
      {viewMode === 'roadmap' && (
        <RoadmapView
          permissions={{ can_view: true, can_create: true, can_edit: true, can_delete: true, can_admin: false }}
        />
      )}

      {/* Analytics View - Velocity, cumulative flow, cycle time, team performance */}
      {viewMode === 'analytics' && (
        <AnalyticsView
          permissions={{ can_view: true, can_create: true, can_edit: true, can_delete: true, can_admin: false }}
        />
      )}

      {/* Bulk Actions Bar - Shows when items are selected */}
      {deletedFilterMode === 'active' && (
        <BulkActionsBar
          selectedItems={selectedItems}
          items={listViewItems}
          onClearSelection={handleClearSelection}
          onRefresh={refreshItems}
          availableUsers={kanbanAssignedUsers}
          availableStatuses={bulkBarStatusOptions}
          availableTags={availableTags}
          showAssign={!readOnly && permissions.can_edit && (bulkActionVisibility?.assign ?? true)}
          showStatus={!readOnly && permissions.can_edit && (bulkActionVisibility?.status ?? true)}
          showPriority={!readOnly && permissions.can_edit && (bulkActionVisibility?.priority ?? true)}
          showTags={!readOnly && permissions.can_edit && (bulkActionVisibility?.tags ?? true)}
          showDueDate={!readOnly && permissions.can_edit && (bulkActionVisibility?.dueDate ?? true)}
          showDelete={!readOnly && permissions.can_delete && (bulkActionVisibility?.delete ?? true)}
          showClose={!readOnly && permissions.can_edit && (bulkActionVisibility?.close ?? true)}
          showExport={bulkActionVisibility?.export ?? true}
          canEdit={!readOnly && permissions.can_edit}
          canDelete={!readOnly && permissions.can_delete}
          primaryColor={primaryColor}
          customActions={
            <>
              {bulkCustomActions}
              {workItemType === 'invoice' ? (
                <button
                  onClick={handleSendPaymentReminder}
                  disabled={sendingReminders}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-amber-100 hover:bg-amber-500/25 transition-colors disabled:opacity-50"
                  title="Send payment reminder to contacts"
                >
                  {sendingReminders ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
                  <span className="hidden sm:inline">Reminder</span>
                </button>
              ) : undefined}
            </>
          }
        />
      )}

      {deletedFilterMode !== 'active' && canViewDeletedItems && (
        <BulkActionsBar
          selectedItems={selectedItems}
          items={listViewItems}
          onClearSelection={handleClearSelection}
          onRefresh={refreshItems}
          showAssign={false}
          showStatus={false}
          showPriority={false}
          showTags={false}
          showDueDate={false}
          showDelete={false}
          showRestore={true}
          showClose={false}
          showExport={true}
          canEdit={false}
          canDelete={false}
          canRestore={true}
          primaryColor={primaryColor}
        />
      )}

      {/* Detail Modal - Comprehensive 8-tab view */}
      {!workItemOpener && selectedItem && (
        <WorkItemDetailModal
          isOpen={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setSelectedItem(null);
          }}
          workItem={selectedItem}
          initialTab={detailInitialTab}
          visibleTabs={detailVisibleTabs}
          onRefresh={refreshItems}
          listModuleContext={moduleContext ?? null}
          activeSolutionKey={solutionKey ?? null}
          containerTourId={detailModalTourId}
        />
      )}

      {/* Location Picker Modal for CalendarView/Timeline drag-drop scheduling */}
      <LocationPickerModal
        isOpen={!!pendingCalendarLocationPick}
        contactId={pendingCalendarLocationPick?.item.contact_id ? Number(pendingCalendarLocationPick.item.contact_id) : null}
        contactName={pendingCalendarLocationPick?.item.contact_name || null}
        jobTitle={pendingCalendarLocationPick?.item.title || ''}
        onSelect={handleCalendarLocationSelected}
        onCancel={handleCalendarLocationPickCancelled}
      />
    </ModulePageShell>
  );
}
